import React, { useState, useMemo } from "react";
import {
    makeStyles,
    shorthands,
    tokens,
    Button,
    mergeClasses,
} from "@fluentui/react-components";
import {
    ArrowClockwise20Regular,
    ArrowMaximize20Regular,
    Attach16Regular,
    Chat20Regular,
    ChevronDown12Regular,
    ChevronRight12Regular,
    Dismiss20Regular,
    Mail20Regular,
    MailInboxAll20Regular,
    Mention16Filled,
    Mention16Regular,
    Mention20Regular,
    Archive20Regular,
    CalendarLtr20Regular,
    ChevronLeft24Regular,
} from "@fluentui/react-icons";
import type { SidebarTabKey } from "./mail/sidebar/sidebarTabs";
import { useNavigate, useSearchParams } from "react-router";
import { MailDetailContent, openMailDetailWindow } from "./mail/MailDetailWindow";
import { hatFeinenZeiger, mailZeileZiehbar } from "./mail/mailZeileZiehen";
import { openEntwurfWindow } from "./mail/EntwurfFenster";
import { canSendMailbox } from "../utils/mailboxAccess";
import { SwipeToDeleteRow } from "./mail/SwipeToDeleteRow";
import { MAIL_ZEILE_AKTIONEN_KLASSE, MAIL_ZEILE_ZEIT_KLASSE, MailZeileLoeschen } from "./mail/MailZeileLoeschen";
import { istEntwurfsLoeschen, loescheMailZeile, MAIL_SCHON_WEG_TEXT, zeigeHoverLoeschen } from "./mail/mailRowActions";
import { MailContextMenu, type MailCtxMenu } from "./mail/MailContextMenu";
import { moveTargetLabel, type MoveTarget } from "./mail/mailMoveTargets";
import {
    startKontaktZuweisen,
    startVorgangErstellen,
    startVorgangZuweisen,
    startSchadenZuordnen,
    type MailListActionCtx,
} from "./mail/mailListActions";
import { schadenOrdnerLink } from "./mail/schadenOrdner";
import { posteingangsPruefung } from "./mail/mailVerlaufItems";
import { SenderAvatar } from "./mail/SenderAvatar";
import { zeilenPerson } from "./mail/zeilenPerson";
// SP6: Kontakt-Sidebar im Lesepanel (Schriftwechsel/Termine/Vorgänge, nur Desktop-Pane).
// SP1(Topbar): eigene Such-Komponente + Suchziel-/Chip-Logik (immer global).
import { MailSearchInput } from "./mail/MailSearchInput";
import { buildFolderNameMap, mailboxChipLabel, resolveSearchTargets } from "./mail/mailSearch";
// SP2(Filterleiste): Absender/Empfänger + Zeitraum (Heute/Gestern/Letzte 7 Tage).
import { MailFilterBar } from "./mail/MailFilterBar";
import { createOnOpenSyncScheduler, type OnOpenSyncScheduler } from "./mail/mailOnOpenSync";
import {
    applyMailFilter,
    EMPTY_MAIL_FILTER,
    isMailFilterActive,
    type MailFilterState,
    type VerknuepfteMails,
} from "./mail/mailFilter";
// TERMINE (P6) — Metaordner „Termine": eigene Listenquelle, reine Sicht.
import {
    ladeTermine,
    matchesTerminSuche,
    terminArtLabel,
    terminZaehlerLabel,
    ungeleseneTermine,
    type TerminMail,
} from "./mail/termineMails";
import { buildMailFolderOrder, TERMINE_FOLDER_KEY } from "./mail/mailFolderTree";
import { mailLinkService } from "../services/mailLinkService";
// L1 — Snapshot einer verknüpften Mail → Listenzeile (+ lokale Suche darin).
import {
    matchesVorgangsMailSuche,
    snapshotZuEmail,
    type VorgangsMail,
} from "../services/vorgangsMails";
// SP8: @-Erwähnungen-Ansicht (Mail-Besprechungs-Mentions) + Topbar-Toggle/Badge.
import { MailMentionsView } from "./mail/MailMentionsView";
import { unreadMentionCount } from "./mail/mailMentions";
// P7 — Konversationen/Serienmails aufklappen: Anzahl, Aufklapp-Zustand, Kindzeilen.
import {
    aufklappLabel,
    istAufklappbar,
    konversationsZeilen,
    alsEinzelnachricht,
    istGesendet,
    gesendetOrdnerIds,
    schalteAufklappen,
    sichtbareAnzahl,
    externeAnzahl,
} from "./mail/mailListConversation";
// P8 — Merkmale der Zeile: Anhang vorhanden, wurde ich erwähnt.
// P7 (PO 14.09.2026) — und: gehört die Mail zu einem Vorgang.
import {
    baueErwaehnungsIndex,
    erwaehnungsMarker,
    hatAnhang,
    vorgangsMarker,
} from "./mail/mailListMarkers";
import { MailVorgangsMarker, useMailVorgangsIndex } from "./mail/MailVorgangsMarker";
// P9 — Lesestatus optimistisch umschalten (Zeilen-Schalter UND Kontextmenü).
import { leseSchalterLabel, setzeLesestatus } from "./mail/mailListReadToggle";
// Cluster ABLAGE (M3): Nacharbeitsliste der dauerhaften Mail-Ablage.
import { MailArchiveView } from "./mail/MailArchiveView";
import { passtZuFilter, type MailArchiveFilter } from "./mail/mailArchiveRows";
import { mailArchiveService, type MailArchiveItem } from "../services/mailArchiveService";
import { openMailContactAssign } from "./mail/MailContactAssignWindow";
import { useDocumentViewer } from "./documents/useDocumentViewer";
import { mailNoteService, isMailDiscussEnabled, type MailMentionItemDto } from "../services/mailNoteService";
// SP1: warme Chat-Palette (Creme/Plum/Gold) auch für den Mail-Bereich.
import { ACTIVE_BG, BG_SOFT, INK, INK2, LINE } from "./chat/chatTheme";
import { canReadMailbox } from "../utils/mailboxAccess";
import { reportMailError } from "../services/mailError";

/**
 * Listenzeile mit Herkunft: Suchtreffer tragen ihr Quell-Postfach mit (die
 * Suche ist immer global), und Zeilen aus dem Vorgangs-Filter (L1) sind
 * `snapshotOnly` — sie haben keinen Rumpf und werden beim Öffnen nachgeladen.
 */
type SearchEmail = Email & {
    srcMailbox?: string;
    snapshotOnly?: boolean;
    vorgang?: { id: number; titel: string } | null;
    /** TERMINE (P6): nur an Zeilen aus dem Termine-Ordner gesetzt. */
    meetingMessageType?: TerminMail["meetingMessageType"];
};
import { openMailSettingsWindow } from "./mail/MailSettingsWindow";
// G4 — „Regel erstellen…" aus dem Kontextmenü der Liste (Cluster REGELN).
import { openMailRegelWindow } from "./mail/MailRegelWindow";
import { I } from "./redesign/kvpIcons";
import { abonniereZuweisung, betrifft } from "./mail/mailAssignmentBus";
import { leseMailDeepLink, MAIL_DEEPLINK_PARAMS } from "./mail/mailDeepLink";
import { useMailContext } from "../context/MailContext";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { useResponsive } from "../hooks/useResponsive";
import { useBildschirmTastatur } from "../hooks/useBildschirmTastatur";
import { useAuth } from "../context/AuthContext";
import { openComposeWindow } from "./mail/ComposeMailWindow";
import BottomSheet from "./calendar/BottomSheet";
// Handy/Tablet wie Outlook mobil: Kopf mit Postfach-Oval, Seitenmenü,
// Auswahlmodus per langem Druck, Aktionsleiste, „Neue Mail"-Knopf.
import { AuswahlKreis, MailAuswahlKopf, MailMobilKopf } from "./mail/mobil/MailMobilKopf";
import { MailPostfachMenue } from "./mail/mobil/MailPostfachMenue";
import { MailAuswahlLeiste, type OrdnerZiel } from "./mail/mobil/MailAuswahlLeiste";
import { NeueMailKnopf } from "./mail/mobil/NeueMailKnopf";
import { useLangerDruck } from "./mail/mobil/langerDruck";
import {
    alleAuswaehlen,
    gemeinsamesPostfach,
    schalteAuswahl,
    sindAlleAusgewaehlt,
    zielLesestatus,
    type MailAuswahl,
} from "./mail/mobil/mailAuswahl";
import { postfachKuerzel, postfachName } from "./mail/mobil/mobilAnzeige";
import { istLeseEintrag, leseEintrag, leseVerlaufSchritt } from "./mail/mobil/leseVerlauf";
import { TerminKarte } from "./mail/mobil/TerminKarte";
import "./mail/mobil/mailMobil.css";

import { graphMailService, groupConversations, einzelneMails, Email, stringToColor, type UnreadMeta, type EntwurfIndex } from "../services/graphMailService";
import { abonniereEntwurfAenderung } from "./mail/entwurfBus";
import { EntwurfMarker } from "./mail/EntwurfMarker";
import { useMailAnsicht } from "./mail/useMailAnsicht";

const now = new Date();

/**
 * Abstand zwischen fertig geladener Liste und dem Nachladen des Thread-Index.
 *
 * Exchange begrenzt die gleichzeitigen Verbindungen je Postfach; ein großes,
 * langsames Postfach hält eine Verbindung 9–20 s. Der Abstand sorgt dafür, dass
 * der Index-Abruf nicht in den Schwanz des Listenladens hineinläuft.
 */
const THREAD_INDEX_VERZOEGERUNG_MS = 1200;

/* ── Brand-Akzente (Refined / kvp-rd): Aubergine + Gold ── */
const BRAND_PURPLE = "#4d2051";
/** Leere Ordnermenge — stabil, damit `istGesendet` ohne Baum nichts neu anlegt. */
const KEINE_ORDNER: ReadonlySet<string> = new Set();
const BRAND_PURPLE_HOVER = "#3a163d";
const BRAND_PURPLE_ACTIVE = "#2d142b";
const BRAND_GOLD = "#f7a823";

/* ────────────────────── Date Grouping ────────────────────── */

function getDateGroup(emailDate: Date, reference: Date): string {
    const refDay = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
    const emlDay = new Date(emailDate.getFullYear(), emailDate.getMonth(), emailDate.getDate());
    const diffDays = Math.floor((refDay.getTime() - emlDay.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Heute";
    if (diffDays === 1) return "Gestern";
    if (diffDays <= 7) return "Letzte 7 Tage";
    // month name + year
    const months = [
        "Januar", "Februar", "März", "April", "Mai", "Juni",
        "Juli", "August", "September", "Oktober", "November", "Dezember",
    ];
    return `${months[emailDate.getMonth()]} ${emailDate.getFullYear()}`;
}

/* ────────────────────── Styles ────────────────────── */

const useStyles = makeStyles({
    /* ── Wurzel: auf Desktop/Tablet horizontaler Split, auf Phone vertikaler Stapel ── */
    root: {
        display: "flex",
        flexDirection: "row",
        height: "100%",
        width: "100%",
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground1,
        overflow: "hidden",
    },
    /* Phone: alles untereinander, volle Breite, scrollende Liste. */
    rootPhone: {
        flexDirection: "column",
    },

    /* ── Left Sidebar (Desktop/Tablet) ── */
    leftSidebar: {
        width: "220px",
        minWidth: "220px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.borderRight("1px", "solid", tokens.colorNeutralStroke2),
        overflowY: "auto",
    },
    /* Tablet: schmalere Spalte, sonst gleiche Logik. */
    leftSidebarTablet: {
        width: "200px",
        minWidth: "200px",
    },
    newBtn: {
        ...shorthands.margin("16px", "16px", "8px", "16px"),
        backgroundColor: BRAND_PURPLE,
        color: "#ffffff",
        ...shorthands.border("1px", "solid", BRAND_PURPLE),
        fontWeight: tokens.fontWeightSemibold as any,
        ":hover": {
            backgroundColor: BRAND_PURPLE_HOVER,
            ...shorthands.borderColor(BRAND_PURPLE_HOVER),
            color: "#ffffff",
        },
        ":hover:active": {
            backgroundColor: BRAND_PURPLE_ACTIVE,
            ...shorthands.borderColor(BRAND_PURPLE_ACTIVE),
            color: "#ffffff",
        },
    },
    navSection: {
        display: "flex",
        flexDirection: "column",
        ...shorthands.padding("4px", "8px"),
    },
    navItem: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        ...shorthands.padding("8px", "12px"),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        cursor: "pointer",
        fontSize: tokens.fontSizeBase300,
        transitionProperty: "background-color",
        transitionDuration: tokens.durationFast,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    navItemActive: {
        backgroundColor: tokens.colorNeutralBackground1Selected,
        fontWeight: tokens.fontWeightSemibold as any,
        ...shorthands.borderLeft("3px", "solid", BRAND_GOLD),
        paddingLeft: "9px",
    },
    navBadge: {
        marginLeft: "auto",
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        fontSize: "11px",
        fontWeight: tokens.fontWeightSemibold as any,
        ...shorthands.borderRadius("10px"),
        ...shorthands.padding("1px", "7px"),
        minWidth: "18px",
        textAlign: "center" as any,
    },
    sectionLabel: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold as any,
        color: tokens.colorNeutralForeground3,
        textTransform: "uppercase" as any,
        letterSpacing: "0.5px",
        ...shorthands.padding("16px", "12px", "4px", "12px"),
        ...shorthands.margin("0", "8px"),
    },

    /* ── Phone-Kopfleiste: „Neue E-Mail" + Postfach-/Ordner-Chips ── */
    phoneHeader: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        ...shorthands.padding("12px", "12px", "8px", "12px"),
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
        flexShrink: 0,
    },
    phoneNewBtn: {
        backgroundColor: BRAND_PURPLE,
        color: "#ffffff",
        ...shorthands.border("1px", "solid", BRAND_PURPLE),
        fontWeight: tokens.fontWeightSemibold as any,
        minHeight: "44px",
        width: "100%",
        ":hover": {
            backgroundColor: BRAND_PURPLE_HOVER,
            ...shorthands.borderColor(BRAND_PURPLE_HOVER),
            color: "#ffffff",
        },
        ":hover:active": {
            backgroundColor: BRAND_PURPLE_ACTIVE,
            ...shorthands.borderColor(BRAND_PURPLE_ACTIVE),
            color: "#ffffff",
        },
    },
    phoneMailboxLabel: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold as any,
        color: tokens.colorNeutralForeground3,
        textTransform: "uppercase" as any,
        letterSpacing: "0.5px",
    },
    /* Horizontale, scrollbare Chip-Leiste (Postfächer × Ordner) für Phone. */
    chipRow: {
        display: "flex",
        flexDirection: "row",
        gap: "8px",
        overflowX: "auto",
        overflowY: "hidden",
        ...shorthands.padding("2px", "0"),
        // iOS-weiche Touch-Scroll-Kinetik
        scrollbarWidth: "none" as any,
        WebkitOverflowScrolling: "touch" as any,
    },
    chip: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        flexShrink: 0,
        minHeight: "44px",
        ...shorthands.padding("0", "14px"),
        ...shorthands.borderRadius("22px"),
        ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase300,
        whiteSpace: "nowrap" as any,
        cursor: "pointer",
    },
    chipActive: {
        backgroundColor: BRAND_PURPLE,
        color: "#ffffff",
        ...shorthands.borderColor(BRAND_PURPLE),
        fontWeight: tokens.fontWeightSemibold as any,
    },
    chipBadge: {
        backgroundColor: "rgba(255,255,255,0.25)",
        color: "#ffffff",
        fontSize: "11px",
        fontWeight: tokens.fontWeightSemibold as any,
        ...shorthands.borderRadius("10px"),
        ...shorthands.padding("0", "6px"),
        minWidth: "16px",
        textAlign: "center" as any,
    },

    /* ── Center area ── */
    centerArea: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minWidth: 0,
    },

    /* ── Topbar am Kopf der Center-Area (SP1-Topbar): [Suchfeld] ····· [Sync] [Einstellungen] ── */
    topBar: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        ...shorthands.padding("4px", "12px"),
        ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
        backgroundColor: tokens.colorNeutralBackground1,
        flexShrink: 0,
        minHeight: "40px",
    },
    /* Suchfeld: Breite = Listen-Spalte (360px inkl. Leisten-Padding → bündig
       über der Liste, nicht breiter); darf auf schmalen Layouts schrumpfen. */
    topBarSearch: {
        flexGrow: 0,
        flexShrink: 1,
        flexBasis: "336px",
        minWidth: 0,
    },
    topBarActions: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        marginLeft: "auto",
        flexShrink: 0,
    },
    /* Eigener Icon+Text-Button (kein Fluent-<Button>) für Sync/Einstellungen. */
    topBarBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        ...shorthands.padding("5px", "10px"),
        ...shorthands.border("0px", "none"),
        ...shorthands.borderRadius("6px"),
        backgroundColor: "transparent",
        color: INK,
        fontSize: "13px",
        fontWeight: 500,
        fontFamily: "inherit",
        whiteSpace: "nowrap" as any,
        cursor: "pointer",
        ":hover": {
            backgroundColor: ACTIVE_BG,
        },
        ":disabled": {
            opacity: 0.5,
            cursor: "default",
            backgroundColor: "transparent",
        },
    },
    topBarBtnIcon: {
        display: "inline-flex",
    },
    /* SP8: aktiver Zustand des @-Erwähnungen-Toggles (Ansicht offen). */
    topBarBtnActive: {
        backgroundColor: ACTIVE_BG,
        color: BRAND_PURPLE,
    },
    /* SP8: @-Toggle links in der Leiste — Badge sitzt oben rechts am Button. */
    mentionToggle: {
        position: "relative" as any,
        flexShrink: 0,
    },
    mentionBadge: {
        position: "absolute" as any,
        top: "-2px",
        right: "-2px",
        minWidth: "16px",
        height: "16px",
        ...shorthands.padding("0", "4px"),
        ...shorthands.borderRadius("8px"),
        backgroundColor: BRAND_GOLD,
        color: "#3a163d",
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: "16px",
        textAlign: "center" as any,
        boxSizing: "border-box" as any,
    },

    /* ── Main Content ── */
    mainContent: {
        display: "flex",
        flexGrow: 1,
        overflow: "hidden",
    },

    /* ── Inbox List (Desktop): feste Spalte neben dem Detail ── */
    inboxPanel: {
        // Vorher starr 360px: zusammen mit Navigations-Sidebar (248px) und
        // Kontext-Leiste (36px) blieben auf einem 1280er Notebook nur ~570px
        // für die Mail. Die Liste darf jetzt bis 300px nachgeben.
        width: "360px",
        minWidth: "300px",
        flexShrink: 1,
        display: "flex",
        flexDirection: "column",
        borderRightWidth: "1px",
        borderRightStyle: "solid",
        borderRightColor: tokens.colorNeutralStroke2,
        backgroundColor: tokens.colorNeutralBackground1,
        overflowY: "auto",
    },
    /* Tablet/Phone: Liste füllt die Breite (fluid), keine feste Pixel-Breite. */
    inboxPanelFluid: {
        width: "100%",
        minWidth: 0,
        flexGrow: 1,
        ...shorthands.borderRight("0", "none", "transparent"),
    },
    dateGroup: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold as any,
        // Das Gerüst der Liste stand in colorNeutralForeground3, der schwächsten
        // Textfarbe von allen. Jetzt echtes Marken-Aubergine (--aub) statt Grau.
        color: "#7d2169",
        letterSpacing: "0.3px",
        ...shorthands.padding("12px", "16px", "4px", "16px"),
        position: "sticky" as any,
        top: "0",
        backgroundColor: tokens.colorNeutralBackground1,
        zIndex: 1,
    },
    mailItem: {
        display: "flex",
        gap: "10px",
        // Links 20 statt 14px: der Lesestatus-Schalter (P9) braucht eine
        // treffbare 24er-Fläche im Zeilenrand. Sie liegt INNERHALB der Zeile —
        // die Liste wird dadurch nicht breiter, nur der Inhalt rückt 6px nach.
        ...shorthands.padding("10px", "14px", "10px", "20px"),
        cursor: "pointer",
        ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
        transitionProperty: "background-color",
        transitionDuration: tokens.durationFast,
        position: "relative" as any,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        // T-65: Beim Hovern (und mit Tastaturfokus in der Zeile) tritt der
        // Papierkorb-Knopf an die Stelle der Uhrzeit — wie in Outlook.
        ":hover .kmvs-row-actions": { display: "flex" },
        ":hover .kmvs-row-actions + .kmvs-row-time": { display: "none" },
        ":focus-within .kmvs-row-actions": { display: "flex" },
        ":focus-within .kmvs-row-actions + .kmvs-row-time": { display: "none" },
    },
    /* Phone/Tablet: größere, gut treffbare Zeilen. */
    mailItemTouch: {
        minHeight: "64px",
        ...shorthands.padding("14px", "16px", "14px", "22px"),
        alignItems: "center",
    },
    /* P7 — die Nachrichten einer aufgeklappten Konversation stehen eingerückt
       unter ihrer Zeile. Der weiche Grund + die Linie links binden sie sichtbar
       an die Zeile darüber, statt sie als eigene Mails der Liste erscheinen zu
       lassen. */
    mailItemChild: {
        // Links 70px = Zeilenrand (20) + Bildspalte (40) + Abstand (10): der
        // Name der Nachricht steht genau unter dem Namen der Kopfzeile.
        ...shorthands.padding("7px", "14px", "7px", "70px"),
        backgroundColor: BG_SOFT,
        borderLeftWidth: "2px",
        borderLeftStyle: "solid",
        borderLeftColor: LINE,
    },
    mailItemSelected: {
        // SP1: warme Auswahl wie die aktive Chat-Konversation (Pfirsich + Gold).
        backgroundColor: ACTIVE_BG,
        borderLeftWidth: "3px",
        borderLeftStyle: "solid",
        borderLeftColor: BRAND_GOLD,
    },
    mailItemLeft: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "4px",
        minWidth: "40px",
    },
    typeIcon: {
        color: tokens.colorNeutralForeground3,
        height: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    /* P9 — der Lesestatus ist ein Schalter, kein Ornament. Der Punkt selbst
       bleibt 8px klein (er soll die Zeile nicht dominieren); treffbar macht ihn
       die 24×24-Fläche des Buttons im linken Zeilenrand. */
    readToggle: {
        position: "absolute" as any,
        left: "0",
        top: "50%",
        transform: "translateY(-50%)",
        width: "24px",
        height: "24px",
        ...shorthands.padding("0"),
        ...shorthands.border("0", "none", "transparent"),
        ...shorthands.borderRadius("50%"),
        backgroundColor: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        // Bewusst OHNE z-index: als positioniertes Element liegt der Schalter
        // ohnehin über den Geschwistern der Zeile — ein z-index würde ihn beim
        // Scrollen zusätzlich über die klebende Datumszeile heben.
        ":hover": {
            backgroundColor: ACTIVE_BG,
        },
        ":focus-visible": {
            outlineWidth: "2px",
            outlineStyle: "solid",
            // Gold auf Weiß sind 1,99:1 — unter der 3:1-Schwelle für
            // Bedienelemente. Aubergine trägt hier ohne neue Farbe.
            outlineColor: BRAND_PURPLE,
            outlineOffset: "-2px",
        },
    },
    /* Aufgeklappte Kindzeilen sind eingerückt — der Schalter rückt mit. */
    readToggleChild: {
        left: "44px",
    },
    unreadDot: {
        width: "8px",
        height: "8px",
        ...shorthands.borderRadius("50%"),
        backgroundColor: BRAND_GOLD,
    },
    /* Gelesen = leerer Ring statt gar nichts: ohne sichtbares Ziel wüsste
       niemand, dass sich eine gelesene Mail zurückstellen lässt. */
    readRing: {
        width: "8px",
        height: "8px",
        ...shorthands.borderRadius("50%"),
        ...shorthands.border("1.5px", "solid", tokens.colorNeutralStroke1),
        backgroundColor: "transparent",
    },
    /* P7/P8 — Merkmale und Aufklapp-Schalter sitzen bei Zeit und Chips, nicht
       im Text: die Zeile bleibt eine Zeile Text, rechts steht der Status. */
    rowMarker: {
        display: "inline-flex",
        alignItems: "center",
        color: INK2,
        flexShrink: 0,
    },
    /* Eine noch ungelesene Erwähnung ist der Grund für den Marker — sie
       bekommt das Gold der Liste, nicht die stille Tinte. */
    rowMarkerMention: {
        color: BRAND_GOLD,
    },
    /* P7 (PO 14.09.2026) — „gehört zu einem Vorgang": der einzige Marker, der
       ein Ziel hat (Klick öffnet den Vorgang), deshalb ein Button in Plum
       statt der stillen Tinte der übrigen Merkmale. Der Klick darf die Zeile
       nicht öffnen (stopPropagation im JSX). */
    /** T-212 (Teil B): „[Entwurf]" wie in Outlook — rot, klickbar. */
    rowMarkerEntwurf: {
        ...shorthands.border("none"),
        ...shorthands.padding("0", "2px"),
        backgroundColor: "transparent",
        color: "#a4262c",
        fontSize: "11px",
        fontWeight: 700,
        lineHeight: "16px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        ":hover": { textDecorationLine: "underline" },
    },
    rowMarkerVorgang: {
        ...shorthands.border("none"),
        ...shorthands.padding("1px"),
        ...shorthands.borderRadius("4px"),
        backgroundColor: "transparent",
        color: BRAND_PURPLE,
        cursor: "pointer",
        lineHeight: 0,
        ":hover": {
            backgroundColor: ACTIVE_BG,
            color: BRAND_PURPLE_HOVER,
        },
    },
    convToggle: {
        display: "inline-flex",
        alignItems: "center",
        gap: "1px",
        ...shorthands.padding("0", "5px", "0", "3px"),
        ...shorthands.border("1px", "solid", ACTIVE_BG),
        ...shorthands.borderRadius("8px"),
        backgroundColor: BG_SOFT,
        color: BRAND_PURPLE,
        fontSize: "10.5px",
        fontWeight: 600,
        lineHeight: "16px",
        cursor: "pointer",
        flexShrink: 0,
        ":hover": {
            backgroundColor: ACTIVE_BG,
        },
        ":focus-visible": {
            outlineWidth: "2px",
            outlineStyle: "solid",
            outlineColor: BRAND_GOLD,
            outlineOffset: "1px",
        },
    },
    /* Zwischenzustand einer aufgeklappten Konversation (lädt / Fehler / leer) —
       auf derselben Einrückung wie die Kindzeilen, damit die Zuordnung stimmt. */
    convHint: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        ...shorthands.padding("8px", "14px", "8px", "70px"),
        backgroundColor: BG_SOFT,
        ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
        fontSize: tokens.fontSizeBase200,
        color: INK2,
    },
    mailItemContent: {
        flexGrow: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "2px",
    },
    mailItemHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    mailSender: {
        // Rangfolge in der Zeile ist Betreff > Absender > Vorschau. Vorher war der
        // Absender 14px und der Betreff 12px — die wichtigste Zeile war die kleinste.
        fontWeight: tokens.fontWeightSemibold as any,
        fontSize: "13px",
        color: INK2,
        whiteSpace: "nowrap" as any,
        overflowX: "hidden" as any,
        textOverflow: "ellipsis",
        flexShrink: 1,
        minWidth: 0,
    },
    /* T-65: Aktionen der Zeile (Papierkorb) — erst beim Hovern sichtbar. */
    rowActions: {
        display: "none",
        alignItems: "center",
        gap: "2px",
        flexShrink: 0,
        marginLeft: "8px",
        // Die Uhrzeit ist 11px hoch; der 24er-Knopf soll die Zeile nicht strecken.
        marginTop: "-5px",
        marginBottom: "-5px",
    },
    mailTime: {
        fontSize: "11px",
        color: tokens.colorNeutralForeground3,
        whiteSpace: "nowrap" as any,
        marginLeft: "8px",
        flexShrink: 0,
    },
    mailSubject: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold as any,
        whiteSpace: "nowrap" as any,
        overflowX: "hidden" as any,
        textOverflow: "ellipsis",
    },
    /* Vorschau + ggf. „Gesendet" in einer Zeile; die Vorschau kürzt, der Hinweis nie. */
    previewZeile: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        minWidth: 0,
        "& > :first-child": { flexGrow: 1, minWidth: 0 },
    },
    /* Outlook Desktop: eigene Nachricht in der Unterhaltung — „Gesendet". */
    gesendetChip: {
        flexShrink: 0,
        ...shorthands.padding("0", "6px"),
        ...shorthands.borderRadius("8px"),
        ...shorthands.border("1px", "solid", LINE),
        backgroundColor: tokens.colorNeutralBackground1,
        color: BRAND_PURPLE,
        fontSize: "10.5px",
        fontWeight: 600,
        lineHeight: "16px",
        whiteSpace: "nowrap",
    },
    mailPreview: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        whiteSpace: "nowrap" as any,
        overflowX: "hidden" as any,
        textOverflow: "ellipsis",
    },
    /* Phone: zwei Vorschauzeilen statt einer (mehr Lesefläche, weniger Abschneiden). */
    mailPreviewWrap: {
        whiteSpace: "normal" as any,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical" as any,
        overflow: "hidden",
    },

    /* ── Mail Detail (SP1: Lese-Panel rechts; Fenster nur via Maximieren/Phone) ── */
    detailPanel: {
        flexGrow: 1,
        // Ohne flexBasis ist die Flex-Basis max-content — die längste
        // Empfängerzeile der geöffneten Mail zog dann an der Listenspalte und
        // ließ sie beim Mailwechsel springen (auf 1280px zwischen 337 und 301px).
        // Mit 0 nimmt das Panel den Rest, die Liste behält ihre 360px.
        flexBasis: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        color: "#000000",
        overflow: "hidden",
    },
    /* SP1: Panel-Kopf (warm, wie Chat-Header): Betreff + Maximieren/Schließen. */
    paneHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "10px",
        ...shorthands.padding("8px", "12px"),
        backgroundColor: BG_SOFT,
        ...shorthands.borderBottom("1px", "solid", LINE),
        flexShrink: 0,
    },
    paneTitle: {
        fontWeight: 600,
        fontSize: "0.9rem",
        color: BRAND_PURPLE_ACTIVE,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: 0,
    },
    /* Suchleiste über der Liste (warm, sticky) — nur noch Phone (kein Topbar-Platz). */
    searchBar: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        ...shorthands.padding("8px", "12px"),
        backgroundColor: BG_SOFT,
        ...shorthands.borderBottom("1px", "solid", LINE),
        position: "sticky" as any,
        top: 0,
        zIndex: 2,
        flexShrink: 0,
    },
    searchInfo: {
        ...shorthands.padding("6px", "14px"),
        fontSize: "12px",
        color: tokens.colorNeutralForeground3,
        backgroundColor: BG_SOFT,
        ...shorthands.borderBottom("1px", "solid", LINE),
    },
    /* SP3 — Skeleton (Cold-Load, wie Outlook) + Refresh-Balken + Sync-Rotation. */
    skelRow: {
        display: "flex",
        gap: "10px",
        alignItems: "center",
        ...shorthands.padding("12px", "14px"),
        ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
    },
    skelPulse: {
        backgroundColor: "#efe9ee",
        animationName: { from: { opacity: 0.45 }, to: { opacity: 1 } },
        animationDuration: "0.9s",
        animationIterationCount: "infinite",
        animationDirection: "alternate" as any,
    },
    skelCircle: { width: "32px", height: "32px", borderRadius: "50%", flexShrink: 0 },
    skelLines: { display: "flex", flexDirection: "column", gap: "8px", flexGrow: 1 },
    skelLine: { height: "10px", ...shorthands.borderRadius("5px") },
    refreshBar: {
        position: "relative" as any,
        height: "3px",
        overflow: "hidden",
        flexShrink: 0,
    },
    refreshBarInner: {
        position: "absolute" as any,
        top: 0,
        left: 0,
        width: "40%",
        height: "100%",
        backgroundColor: BRAND_GOLD,
        animationName: {
            "0%": { transform: "translateX(-100%)" },
            "100%": { transform: "translateX(350%)" },
        },
        animationDuration: "1.1s",
        animationIterationCount: "infinite",
        animationTimingFunction: "ease-in-out",
    },
    syncSpin: {
        display: "inline-flex",
        animationName: { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
        animationDuration: "0.9s",
        animationIterationCount: "infinite",
        animationTimingFunction: "linear",
    },
    /* Quell-Postfach-Chip — an JEDEM Suchtreffer (Suche ist immer global). */
    mailboxChip: {
        display: "inline-block",
        ...shorthands.padding("0", "6px"),
        ...shorthands.borderRadius("8px"),
        backgroundColor: ACTIVE_BG,
        color: BRAND_PURPLE,
        fontSize: "10.5px",
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    // Mail-SP2: zweiter, dezenter Ordner-Chip neben dem Postfach-Chip in den
    // Suchtreffern. Outline-Variante (statt gefüllt) → optisch abgesetzt vom
    // Postfach-Chip. Lange Ordnernamen werden per CSS gekürzt (max-width +
    // Ellipsis), der Volltext steht im title-Attribut (keine harte Zeichengrenze).
    folderChip: {
        display: "inline-block",
        maxWidth: "104px",
        ...shorthands.overflow("hidden"),
        textOverflow: "ellipsis",
        ...shorthands.padding("0", "6px"),
        ...shorthands.borderRadius("8px"),
        ...shorthands.border("1px", "solid", ACTIVE_BG),
        backgroundColor: BG_SOFT,
        color: BRAND_PURPLE,
        fontSize: "10.5px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        verticalAlign: "middle",
    },
    emptyDetail: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: tokens.colorNeutralForeground3,
        gap: "12px",
        ...shorthands.padding("24px"),
        textAlign: "center" as any,
    },

    /* ── Leerer Posteingang ── */
    emptyList: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flexGrow: 1,
        color: tokens.colorNeutralForeground3,
        gap: "10px",
        ...shorthands.padding("40px", "24px"),
        textAlign: "center" as any,
    },
});

/* ────────────────────── Component ────────────────────── */

export const MailView: React.FC = () => {
    const styles = useStyles();
    const { isPhone, isTablet, width } = useResponsive();
    // Einmal je Montage: ob Maus/Touchpad da ist, ändert sich im Betrieb kaum.
    const [feinerZeiger] = useState(hatFeinenZeiger);
    // Tablet-Darstellung (Liste → Vollansicht mit „Zurück"): die Tablet-Breite
    // der App (769–1024) UND ein iPad quer (bis 1366, nur Finger, kein
    // Trackpad) — dort war der Desktop-Split zwischen Liste und Mail gequetscht.
    const tabletLayout = isTablet || (!isPhone && !feinerZeiger && width <= 1366);
    // Ordner-Auswahl liegt jetzt im MailContext (gespeist aus dem NavLayout-
    // Mail-Menü, PR4). `activeFolder=null` → Standard = Posteingang (ungelesen).
    const { currentMailbox, setCurrentMailbox, activeFolder, setActiveFolder, mailRefreshTick, mailboxes, refreshFolders, folderTrees, searchQuery, setSearchQuery, unreadByMailbox } = useMailContext();
    const { isAdmin } = useAuth();
    const { openWindow } = useWindowManager();
    // P7 (PO 14.09.2026): der Vorgangs-Marker an der Zeile öffnet den Vorgang.
    const navigate = useNavigate();
    const [selectedEmailId, setSelectedEmailId] = useState<string>("");
    const [emails, setEmails] = React.useState<Email[]>([]);
    // Zu welchem Postfach gehört `emails` gerade? Nach einem Postfachwechsel
    // steht bis zum Eintreffen der neuen Seite noch die alte Liste im State —
    // der Vorgangs-Index (P7) darf deren Konversationen nicht unter dem neuen
    // Postfach nachfragen.
    const listenMailboxRef = React.useRef<string | null>(null);
    // SP3: echter Lade-Zustand — treibt Skeleton (Cold-Load) + Refresh-Balken.
    const [isLoading, setIsLoading] = React.useState(false);
    // SP3: manueller Sync (Server-Delta + Cache-Verwurf + Refetch).
    const [syncing, setSyncing] = React.useState(false);

    // B10 — Paginierung/Transparenz der Ungelesen-Ansicht: Meta der letzten
    // Seite (hasMore/nextSkip), kumulierte RBAC-Ausblendungen und die
    // UNGRUPPIERTE Roh-Akkumulation (Threads werden über alles neu gruppiert).
    const [unreadMeta, setUnreadMeta] = React.useState<UnreadMeta | null>(null);
    const [hiddenTotal, setHiddenTotal] = React.useState(0);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const unreadRawRef = React.useRef<Email[]>([]);

    // ── P7: aufklappbare Konversationen ──────────────────────────────────────
    // `groupConversations` faltet einen Thread auf eine Zeile; hier steht, welche
    // Zeile der Nutzer aufgeklappt hat und welche Nachrichten dazu schon
    // nachgeladen sind. Bewusst im Component-State und nicht im Service: der
    // Service kennt nur Daten, „offen/zu" ist eine Eigenschaft DIESER Ansicht.
    // Schlüssel ist überall die `conversationId` — sie überlebt das Neugruppieren
    // nach „Mehr laden", eine Message-Id (der Repräsentant) täte das nicht.
    const [expandedConvs, setExpandedConvs] = React.useState<ReadonlySet<string>>(new Set());
    const [convChildren, setConvChildren] = React.useState<Record<string, Email[]>>({});
    const [convLoading, setConvLoading] = React.useState<ReadonlySet<string>>(new Set());
    const [convError, setConvError] = React.useState<ReadonlySet<string>>(new Set());
    const resetConversations = React.useCallback(() => {
        setExpandedConvs((prev) => (prev.size ? new Set<string>() : prev));
        setConvChildren((prev) => (Object.keys(prev).length ? {} : prev));
        setConvLoading((prev) => (prev.size ? new Set<string>() : prev));
        setConvError((prev) => (prev.size ? new Set<string>() : prev));
    }, []);

    // B4: Listen-Fehler nicht mehr verschlucken — sichtbarer Zustand + Retry.
    const [loadError, setLoadError] = React.useState(false);
    const [retryTick, setRetryTick] = React.useState(0);
    const activeFolderIdRef = React.useRef<string | null>(null);
    const listenQuelleRef = React.useRef("");
    // Rechtsklick-Kontextmenü auf einer Mail-Zeile (Verschieben/Archivieren/Gelesen).
    const [ctxMenu, setCtxMenu] = React.useState<MailCtxMenu | null>(null);
    // Fehlerbanner für Kontextmenü-Aktionen (Move/Archiv/Markieren) — auto-verwerfend.
    const [actionError, setActionError] = React.useState<string | null>(null);
    React.useEffect(() => {
        if (!actionError) return;
        const t = setTimeout(() => setActionError(null), 5000);
        return () => clearTimeout(t);
    }, [actionError]);
    // Cluster POSTFACH: neutraler Hinweis (kein Fehler) — etwa „Erkennung nicht
    // verfügbar" oder „Anhänge konnten nicht geladen werden". Bewusst getrennt
    // vom Fehlerbanner: der Ablauf geht weiter, er wird nur ehrlich benannt.
    const [actionNote, setActionNote] = React.useState<string | null>(null);
    React.useEffect(() => {
        if (!actionNote) return;
        const t = setTimeout(() => setActionNote(null), 6000);
        return () => clearTimeout(t);
    }, [actionNote]);
    // Message-Id der Mail, deren asynchrone Vorstufe (Erkennung/Anhänge) gerade
    // läuft. Zwischen Klick und Fenster liegen bis zu zwei Netz-Roundtrips —
    // ohne Rückmeldung wirkt das Menü wie ohne Funktion, und ein zweiter
    // Rechtsklick startete den Ablauf ein zweites Mal.
    const [pendingAction, setPendingAction] = React.useState<string | null>(null);

    // Rückgängig-Hinweis nach dem Löschen. Bewusst KEIN Bestätigungsdialog: der
    // Move in den Papierkorb ist reversibel, und ein Dialog vor jeder Löschung
    // macht besonders die Wisch-Geste auf dem Handy unbrauchbar.
    //
    // `newId` ist entscheidend: Graph vergibt beim Verschieben eine NEUE
    // Message-Id, die alte ist danach tot. Ohne die neue Id gäbe es keinen
    // Rückweg. `from` merkt sich den Quell-Ordner, damit die Mail wieder dort
    // landet, wo sie herkam — nicht pauschal im Posteingang.
    const [undoDelete, setUndoDelete] = React.useState<
        { mailbox: string; newId: string; from: string; subject: string } | null
    >(null);
    React.useEffect(() => {
        if (!undoDelete) return;
        const t = setTimeout(() => setUndoDelete(null), 8000);
        return () => clearTimeout(t);
    }, [undoDelete]);

    // SP1 — Lese-Panel: auf dem Desktop wird die angeklickte Mail im rechten
    // Panel gelesen (Liste bleibt sichtbar); Phone/Tablet behalten das Fenster.
    const [readingEmail, setReadingEmail] = React.useState<Email | null>(null);
    // SP2: Suchtreffer können aus einem ANDEREN Postfach stammen — das Panel
    // liest dann gegen dieses Quell-Postfach statt gegen currentMailbox.
    const [readingMailbox, setReadingMailbox] = React.useState<string | null>(null);
    // Klick auf die KOPFZEILE einer Unterhaltung liest die ganze Unterhaltung
    // (neueste oben, getrennt durch Linien); Klick auf eine Nachricht darunter
    // nur diese. `readingEinzeln` sagt dem Lesebereich, welches von beiden.
    const [readingEinzeln, setReadingEinzeln] = React.useState(true);
    const [unterhaltungGewaehlt, setUnterhaltungGewaehlt] = React.useState(false);
    // Desktop: Liste + Lesebereich nebeneinander. Handy/Tablet: die Liste, und
    // eine geöffnete Mail legt sich als Vollansicht darüber („Zurück").
    const usePane = !isPhone && !tabletLayout;

    // ── Handy: Auswahlmodus (langer Druck), Seitenmenü, Filter-Sheet ──
    const [auswahl, setAuswahl] = React.useState<MailAuswahl<SearchEmail> | null>(null);
    const [postfachMenueOffen, setPostfachMenueOffen] = React.useState(false);
    const [filterSheetOffen, setFilterSheetOffen] = React.useState(false);
    // Handy-Vollansicht: die Leiste rechts weicht (volle Breite für die Mail);
    // ihre Reiter öffnet das „…"-Menü im Kopf. `leseReiter` = offener Reiter.
    const [leseReiter, setLeseReiter] = React.useState<SidebarTabKey | null>(null);

    // ── Handy: „Zurück" führt aus der Mail zur Liste ──
    // Egal ob der Knopf in der Mail, die Pfeile der App-Kopfzeile oder die
    // Zurück-Geste von Browser/iOS: Beim Öffnen der Vollansicht kommt ein
    // eigener Verlaufseintrag dazu, und dessen Verlassen (popstate) schließt
    // die Mail. Der Eintrag trägt einen um eins höheren `idx` — so führt der
    // App-Pfeil (`smartBack` liest `history.state.idx`) per navigate(-1)
    // genau dorthin zurück. Desktop und Tablet bleiben unberührt.
    const lesenOffen = isPhone && !!readingEmail;
    React.useEffect(() => {
        if (!isPhone) return;
        const st = (window.history.state ?? null) as Record<string, unknown> | null;
        const schritt = leseVerlaufSchritt(lesenOffen, st);
        if (schritt === "push") {
            window.history.pushState(leseEintrag(st), "");
        } else if (schritt === "back") {
            // Geschlossen über Knopf, Archivieren, Löschen …: den Eintrag wieder abbauen.
            window.history.back();
        }
    }, [lesenOffen, isPhone]);
    React.useEffect(() => {
        if (!isPhone) return;
        const beiZurueck = (e: PopStateEvent) => {
            if (!istLeseEintrag(e.state as Record<string, unknown> | null)) setReadingEmail(null);
        };
        window.addEventListener("popstate", beiZurueck);
        return () => window.removeEventListener("popstate", beiZurueck);
    }, [isPhone]);
    // Der Melde-Knopf rückt in der Vollansicht klein an den Rand (mailMobil.css).
    React.useEffect(() => {
        if (!lesenOffen) return;
        document.documentElement.classList.add("mm-lesen-offen");
        return () => document.documentElement.classList.remove("mm-lesen-offen");
    }, [lesenOffen]);
    React.useEffect(() => {
        setLeseReiter(null);
    }, [readingEmail?.id]);
    const langerDruck = useLangerDruck();
    const tastatur = useBildschirmTastatur(isPhone);
    // Die Auswahl gilt für die Liste, die man gerade sieht.
    React.useEffect(() => {
        setAuswahl(null);
    }, [currentMailbox, activeFolder, isPhone]);

    // ── SP8: @-Erwähnungen-Ansicht — nur wenn „Mail besprechen" freigeschaltet
    //   ist (Hintergrund-Feature, Entscheidung 23/24: nur KMVS-Mentions). Die
    //   Liste speist Badge (ungelesene) UND die Ansicht aus einer Quelle. ──
    const mailDiscussOn = React.useMemo(() => isMailDiscussEnabled(), []);
    const [mentionsOpen, setMentionsOpen] = React.useState(false);
    const [mentions, setMentions] = React.useState<MailMentionItemDto[]>([]);
    const [mentionsLoading, setMentionsLoading] = React.useState(false);
    const unreadMentions = React.useMemo(() => unreadMentionCount(mentions), [mentions]);
    // P8 — dieselbe Quelle speist jetzt auch die Listenzeile: das Mentions-DTO
    // trägt `mailbox` + `messageId`, eine Erwähnung ist also eindeutig einer
    // Zeile zuzuordnen. Ohne freigeschaltetes „Mail besprechen" bleibt die Liste
    // leer und damit auch der Index — dann markiert die Liste schlicht nichts.
    const mentionIndex = React.useMemo(() => baueErwaehnungsIndex(mentions), [mentions]);

    // ── T-29: Darstellung „Unterhaltungen" (Standard) oder „Einzelne Mails" ──
    // Pro Person gespeichert. „Einzeln" = jede Mail eine Zeile, kein Aufklappen,
    // und der Lesebereich zeigt nur die angeklickte Nachricht. Gewählt wird
    // sie seit T-61 nur noch in den Mail-Einstellungen (Reiter „Darstellung").
    const { ansicht } = useMailAnsicht();
    const einzeln = ansicht === "einzeln";

    // ── Thread-Index: conversationId → eigene Antworten in „Gesendet" ────────
    // Die Faltung sieht nur den geladenen Ordner, und dort liegt bei fast jedem
    // Thread genau EINE Nachricht — am Ordner „Erneut erinnern aktiviert"
    // (cll@courte.com) gemessen zeigten 47 von 61 Zeilen deshalb keinen
    // Aufklapp-Pfeil, obwohl der Thread 2 bis 6 Nachrichten hatte. Sie lagen
    // alle in „Gesendet".
    //
    // Bewusst ein EIGENER Effekt, genau wie der Erwähnungs-Index: die Liste
    // rendert sofort, der Index trifft später ein und setzt die Pfeile nach.
    // Bleibt er aus (Fehler, Demo-Postfach), verhält sich die Liste wie vorher.
    //
    // NACHRANGIG, nie gleichzeitig mit dem Listenladen: Exchange begrenzt die
    // GLEICHZEITIGEN Verbindungen je Postfach. Am 01.09.2026 an cll@courte.com
    // gemessen — einzeln nacheinander gingen alle Abrufe durch (200), vier
    // gleichzeitig ergaben 1× 200 und 3× 429 („Too many concurrent connections
    // opened. Cannot open mailbox."). Beim Postfachwechsel laufen ohnehin schon
    // Ordnerbaum, Posteingang und Delta; ein vierter Abruf zur selben Zeit
    // kippte bei diesem großen, langsamen Postfach (9–20 s je Abruf) den
    // gesamten Wechsel.
    //
    // Deshalb erst NACH dem Listenladen und mit Abstand. Die Zahl ist Beiwerk —
    // sie darf warten, die Liste nicht.
    const [threadIndex, setThreadIndex] = React.useState<Record<string, string[]>>({});
    // Zählt fertige Thread-Index-Läufe — der Entwurfs-Index startet erst danach
    // (nie gleichzeitig, s. Kommentar oben zur Exchange-Verbindungsgrenze).
    const [threadIndexLauf, setThreadIndexLauf] = React.useState(0);
    React.useEffect(() => {
        // T-29: In der Einzelansicht gibt es nichts aufzuklappen — der Abruf
        // spart dann eine der knappen Graph-Verbindungen des Postfachs.
        if (!currentMailbox || einzeln) {
            setThreadIndex({});
            setThreadIndexLauf((n) => n + 1);
            return;
        }
        if (isLoading) return;
        let verworfen = false;
        const timer = window.setTimeout(() => {
            graphMailService
                .fetchThreadCounts(currentMailbox)
                .then((karte) => {
                    if (!verworfen) setThreadIndex(karte);
                })
                .catch(() => {
                    if (!verworfen) setThreadIndex({});
                })
                .finally(() => {
                    if (!verworfen) setThreadIndexLauf((n) => n + 1);
                });
        }, THREAD_INDEX_VERZOEGERUNG_MS);
        return () => {
            verworfen = true;
            window.clearTimeout(timer);
        };
    }, [currentMailbox, mailRefreshTick, isLoading, einzeln]);

    // ── T-212 (Teil B): Entwurfs-Index — conversationId → Antwort-Entwurf ──
    // Kennzeichnet Mails, zu denen ein Antwort-Entwurf liegt („[Entwurf]", wie
    // Outlook). Nachrangig: erst nach dem Thread-Index. Nur mit Senderecht —
    // ohne ließe sich der Entwurf ohnehin nicht fortsetzen. Der Delta-Poll
    // meldet Entwürfe nicht; Speichern/Senden/Löschen melden sich über den Bus.
    const [entwurfIndex, setEntwurfIndex] = React.useState<EntwurfIndex>({});
    const [entwurfTick, setEntwurfTick] = React.useState(0);
    const darfEntwuerfe =
        !!currentMailbox &&
        mailboxes.some((m) => m.mailboxAddress.toLowerCase() === currentMailbox.toLowerCase() && canSendMailbox(m));
    React.useEffect(
        () =>
            abonniereEntwurfAenderung((mb) => {
                if (!currentMailbox || mb.toLowerCase() !== currentMailbox.toLowerCase()) return;
                setEntwurfTick((n) => n + 1);
                // T-231: Im Ordner „Entwürfe" auch die Liste neu laden — sonst
                // bleibt ein im Fenster gesendeter/gelöschter Entwurf als tote
                // Zeile stehen und jedes Löschen endet im 404. Der Cache des
                // Ordners ist durch invalidateMailbox schon verworfen.
                if (activeFolderIdRef.current === "drafts") setRetryTick((t) => t + 1);
            }),
        [currentMailbox],
    );
    React.useEffect(() => {
        if (!currentMailbox || !darfEntwuerfe) {
            setEntwurfIndex({});
            return;
        }
        if (isLoading) return;
        let verworfen = false;
        graphMailService
            .fetchDraftIndex(currentMailbox)
            .then((karte) => {
                if (!verworfen) setEntwurfIndex(karte);
            })
            .catch(() => {
                if (!verworfen) setEntwurfIndex({});
            });
        return () => {
            verworfen = true;
        };
        // `mailRefreshTick`/`isLoading` laufen über `threadIndexLauf` mit ein.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMailbox, darfEntwuerfe, threadIndexLauf, entwurfTick]);

    const loadMentions = React.useCallback(async () => {
        if (!mailDiscussOn) return;
        setMentionsLoading(true);
        try {
            setMentions(await mailNoteService.listMyMentions());
        } catch (err) {
            reportMailError({
                area: "mail",
                action: "load-mentions",
                error: err,
                fallbackMessage: "Erwähnungen konnten nicht geladen werden.",
            });
        } finally {
            setMentionsLoading(false);
        }
    }, [mailDiscussOn]);

    // Badge initial füllen (einmal), damit die Zahl ohne Öffnen der Ansicht stimmt.
    React.useEffect(() => {
        void loadMentions();
    }, [loadMentions]);

    // ── Cluster ABLAGE (M3): Ansicht „Ablage" — die Nacharbeitsliste der
    //   dauerhaften Mail-Ablage. Geladen wird erst beim Öffnen (T-294). ──
    const [archiveOpen, setArchiveOpen] = React.useState(false);
    const [archiveItems, setArchiveItems] = React.useState<MailArchiveItem[]>([]);
    const [archiveLoading, setArchiveLoading] = React.useState(false);
    const [archiveError, setArchiveError] = React.useState<string | null>(null);
    const [archiveFilter, setArchiveFilter] = React.useState<MailArchiveFilter>("ohneKontakt");
    const [archiveSuche, setArchiveSuche] = React.useState("");
    const [archiveBusyId, setArchiveBusyId] = React.useState<number | null>(null);
    const openDocumentViewer = useDocumentViewer();

    const loadArchive = React.useCallback(async () => {
        setArchiveLoading(true);
        setArchiveError(null);
        try {
            // Bewusst OHNE Serverfilter: die vier Pillen filtern lokal auf
            // derselben Menge — ein Wechsel der Pille braucht keinen Abruf.
            const { items } = await mailArchiveService.list({ limit: 200 });
            setArchiveItems(items);
        } catch (err: unknown) {
            setArchiveError(reportMailError({
                area: "mail",
                action: "load-list",
                error: err,
                fallbackMessage: "Die Mail-Ablage konnte nicht geladen werden.",
            }));
        } finally {
            setArchiveLoading(false);
        }
    }, []);

    // T-294 (12.10.2026): Keine Zahl mehr am Ablage-Knopf — und damit auch
    // kein Vorab-Laden beim Öffnen des Postfachs. Die Ablage lädt, wenn man
    // sie öffnet (Knopf in der Kopfleiste).

    const archiveSichtbar = React.useMemo(() => {
        const nadel = archiveSuche.trim().toLowerCase();
        return archiveItems.filter((i) => {
            if (!passtZuFilter(i, archiveFilter)) return false;
            if (!nadel) return true;
            return (
                (i.subject ?? "").toLowerCase().includes(nadel) ||
                (i.senderName ?? "").toLowerCase().includes(nadel) ||
                (i.senderEmail ?? "").toLowerCase().includes(nadel)
            );
        });
    }, [archiveItems, archiveFilter, archiveSuche]);

    /** Die abgelegte `.eml` im vorhandenen Mail-Datei-Viewer öffnen. */
    const oeffneAblageDokument = React.useCallback(
        (item: MailArchiveItem) => {
            if (!item.documentId) return;
            openDocumentViewer({
                id: item.documentId,
                fileName: item.fileName ?? `${item.subject ?? "Mail"}.eml`,
                mimeType: "message/rfc822",
            });
        },
        [openDocumentViewer],
    );

    /** Kontakt nachtragen — über das bestehende Zuweisungs-Popup. */
    const weiseAblageKontaktZu = React.useCallback(
        (item: MailArchiveItem) => {
            openMailContactAssign(openWindow, {
                senderEmail: item.senderEmail,
                senderName: item.senderName,
                aktuelleZuweisung: item.contact,
                onResolved: async (ergebnis) => {
                    if (!ergebnis.kontakt) return;
                    try {
                        const { archive } = await mailArchiveService.assign(item.id, {
                            contactId: ergebnis.kontakt.id,
                        });
                        setArchiveItems((prev) => prev.map((x) => (x.id === archive.id ? archive : x)));
                    } catch (err: unknown) {
                        setArchiveError(reportMailError({
                            area: "mail",
                            action: "assign",
                            error: err,
                            fallbackMessage: "Der Kontakt konnte nicht zugewiesen werden.",
                        }));
                    }
                },
            });
        },
        [openWindow],
    );

    /** Erneut versuchen — auch für übersprungene (zu große) Mails (E11). */
    const wiederholeAblage = React.useCallback(async (item: MailArchiveItem) => {
        setArchiveBusyId(item.id);
        try {
            const { archive } = await mailArchiveService.retry(item.id);
            setArchiveItems((prev) => prev.map((x) => (x.id === archive.id ? archive : x)));
        } catch (err: unknown) {
            setArchiveError(reportMailError({
                area: "mail",
                action: "archive",
                error: err,
                fallbackMessage: "Die Ablage konnte nicht wiederholt werden.",
            }));
        } finally {
            setArchiveBusyId(null);
        }
    }, []);

    // ── SP1(Topbar): Suche (debounced) — IMMER global über alle lesbaren Postfächer ──
    // SP4: `searchQuery`/`setSearchQuery` liegen jetzt zentral im MailContext, damit
    // die mobile Bottom-Mail-Leiste (im NavigationLayout) und diese Liste sich EINEN
    // Such-State teilen (eine Quelle der Wahrheit, kein Race).
    const [searchResults, setSearchResults] = React.useState<SearchEmail[] | null>(null);
    const [searching, setSearching] = React.useState(false);
    // SP2(Filterleiste): Absender/Empfänger + Zeitraum — clientseitig auf die
    // geladene Liste bzw. die Suchtreffer, mit der globalen Suche kombinierbar.
    const [mailFilter, setMailFilter] = React.useState<MailFilterState>(EMPTY_MAIL_FILTER);
    // ── L1 — Filter „Vorgänge": eigene LISTENQUELLE statt Sieb ──
    //
    // Bis 23.08.2026 holte der Filter nur die Zuordnungsschlüssel und siebte
    // damit die BEREITS GELADENE Seite des AKTUELLEN Ordners. Mails im Archiv,
    // in Unterordnern oder unterhalb der geladenen Seite tauchten nie auf, und
    // bei leerem Posteingang blieb der Filter leer, obwohl es Vorgangs-Mails
    // gab. Jetzt liefert `GET /mail/links/verknuepft` die verknüpften Mails
    // selbst (mit Snapshot, seitenweise über alle Ordner) — ist der Filter an,
    // ist DAS die Quelle der Liste.
    const nurVorgaenge = !!mailFilter.nurVorgaenge;
    const [vorgangsMails, setVorgangsMails] = React.useState<VorgangsMail[]>([]);
    const [vorgangsCursor, setVorgangsCursor] = React.useState<string | null>(null);
    const [vorgangsLoading, setVorgangsLoading] = React.useState(false);
    const [vorgangsMoreLoading, setVorgangsMoreLoading] = React.useState(false);
    const [vorgangsError, setVorgangsError] = React.useState<string | null>(null);
    // Lauf-Zähler: eine verspätete Antwort zum ALTEN Postfach darf die Liste des
    // neuen nicht überschreiben. Die Busy-Marke verhindert, dass der
    // Scroll-Sentinel dieselbe Seite mehrfach anfordert.
    const vorgangsLaufRef = React.useRef(0);
    const vorgangsBusyRef = React.useRef(false);

    const ladeVorgangsSeite = React.useCallback(
        async (mailbox: string, cursor: string | null) => {
            if (cursor && vorgangsBusyRef.current) return;
            const lauf = ++vorgangsLaufRef.current;
            vorgangsBusyRef.current = true;
            if (cursor) setVorgangsMoreLoading(true);
            else setVorgangsLoading(true);
            setVorgangsError(null);
            try {
                const seite = await mailLinkService.listVerknuepft(mailbox, { cursor });
                if (vorgangsLaufRef.current !== lauf) return;
                const neue = seite.items.map(snapshotZuEmail);
                setVorgangsMails((prev) => {
                    if (!cursor) return neue;
                    // An der Seitengrenze kann dieselbe Nachricht ein zweites Mal
                    // kommen (mehrere Verknüpfungen je Mail) — hier dedupliziert.
                    const bekannt = new Set(prev.map((m) => m.id));
                    return [...prev, ...neue.filter((m) => !bekannt.has(m.id))];
                });
                setVorgangsCursor(seite.nextCursor);
            } catch (err) {
                if (vorgangsLaufRef.current !== lauf) return;
                setVorgangsError(reportMailError({
                    area: "mail",
                    action: "load-linked-mails",
                    error: err,
                    fallbackMessage: "Vorgangs-Mails konnten nicht geladen werden.",
                }));
                // Kein Cursor mehr → kein stilles Weiter-Nachladen ins Leere.
                setVorgangsCursor(null);
            } finally {
                if (vorgangsLaufRef.current === lauf) {
                    vorgangsBusyRef.current = false;
                    setVorgangsLoading(false);
                    setVorgangsMoreLoading(false);
                }
            }
        },
        [],
    );

    // Erste Seite laden, sobald der Filter angeht; Postfachwechsel lädt neu.
    React.useEffect(() => {
        if (!nurVorgaenge || !currentMailbox) {
            // Filter aus: Quelle verwerfen, damit ein späteres Einschalten nicht
            // kurz die Mails des vorigen Postfachs zeigt.
            vorgangsLaufRef.current++;
            vorgangsBusyRef.current = false;
            setVorgangsMails((prev) => (prev.length ? [] : prev));
            setVorgangsCursor(null);
            setVorgangsError(null);
            setVorgangsLoading(false);
            setVorgangsMoreLoading(false);
            return;
        }
        void ladeVorgangsSeite(currentMailbox, null);
    }, [nurVorgaenge, currentMailbox, ladeVorgangsSeite]);

    // „Beim Scrollen nachladen": ein Sentinel am Listenende fordert die nächste
    // Keyset-Seite an. Die sichtbare Schaltfläche daneben bleibt als Rückfall
    // (Tastatur, kein IntersectionObserver).
    const vorgangsSentinelRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => {
        if (!nurVorgaenge || !currentMailbox || !vorgangsCursor) return;
        const node = vorgangsSentinelRef.current;
        if (!node || typeof IntersectionObserver === "undefined") return;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    void ladeVorgangsSeite(currentMailbox, vorgangsCursor);
                }
            },
            { rootMargin: "200px" },
        );
        obs.observe(node);
        return () => obs.disconnect();
    }, [nurVorgaenge, currentMailbox, vorgangsCursor, ladeVorgangsSeite]);

    // ── TERMINE (P6): der Metaordner „Termine" als eigene LISTENQUELLE ──
    //
    // Zusagen und Absagen auf Termineinladungen verstopfen den Posteingang; sie
    // erscheinen deshalb ZUSÄTZLICH gebündelt. Ausdrücklich als SICHT: Es wird
    // nichts im Postfach verschoben, nichts gelöscht — dieselben Mails stehen
    // weiterhin im Posteingang und tauchen hier ein zweites Mal auf.
    //
    // Erreichbar auf zwei Wegen, die auf DENSELBEN Zustand laufen: der
    // Umschalter in der Funktionsleiste (hier) und der Ordner-Eintrag „Termine"
    // (`activeFolder.id === TERMINE_FOLDER_KEY`, gebaut in mailFolderTree.ts).
    const [termineOpen, setTermineOpen] = React.useState(false);
    const [termineMails, setTermineMails] = React.useState<TerminMail[]>([]);
    const [termineMeta, setTermineMeta] = React.useState<UnreadMeta | null>(null);
    const [termineLoading, setTermineLoading] = React.useState(false);
    const [termineMoreLoading, setTermineMoreLoading] = React.useState(false);
    const [termineError, setTermineError] = React.useState<string | null>(null);
    // Lauf-Zähler wie bei den Vorgangs-Mails: eine verspätete Antwort zum ALTEN
    // Postfach darf die Liste des neuen nicht überschreiben.
    const termineLaufRef = React.useRef(0);
    const termineBusyRef = React.useRef(false);
    // Der Vorgangs-Filter hat Vorrang: Er stellt bereits die Listenquelle, zwei
    // Quellen gleichzeitig gäbe es nicht — dann bliebe die Termine-Sicht eine
    // Behauptung ohne sichtbare Liste.
    const termineAktiv =
        !nurVorgaenge && (termineOpen || activeFolder?.id === TERMINE_FOLDER_KEY);

    const ladeTermineSeite = React.useCallback(async (mailbox: string, skip: number | null) => {
        if (skip !== null && termineBusyRef.current) return;
        const lauf = ++termineLaufRef.current;
        termineBusyRef.current = true;
        if (skip !== null) setTermineMoreLoading(true);
        else setTermineLoading(true);
        setTermineError(null);
        try {
            const seite = await ladeTermine(mailbox, skip ?? 0);
            if (termineLaufRef.current !== lauf) return;
            setTermineMails((prev) => {
                if (skip === null) return seite.mails;
                // An der Seitengrenze kann dieselbe Nachricht ein zweites Mal
                // kommen — hier dedupliziert.
                const bekannt = new Set(prev.map((m) => m.id));
                return [...prev, ...seite.mails.filter((m) => !bekannt.has(m.id))];
            });
            setTermineMeta(seite.meta);
        } catch (err) {
            if (termineLaufRef.current !== lauf) return;
            setTermineError((err as Error)?.message || "Termine konnten nicht geladen werden.");
            // Kein „Mehr laden" mehr anbieten, das ins Leere liefe.
            setTermineMeta(null);
        } finally {
            if (termineLaufRef.current === lauf) {
                termineBusyRef.current = false;
                setTermineLoading(false);
                setTermineMoreLoading(false);
            }
        }
    }, []);

    // Erste Seite laden, sobald die Sicht angeht; Postfachwechsel lädt neu.
    React.useEffect(() => {
        if (!termineAktiv || !currentMailbox) {
            // Aus: Quelle verwerfen, damit ein späteres Einschalten nicht kurz
            // die Termine des vorigen Postfachs zeigt.
            termineLaufRef.current++;
            termineBusyRef.current = false;
            setTermineMails((prev) => (prev.length ? [] : prev));
            setTermineMeta(null);
            setTermineError(null);
            setTermineLoading(false);
            setTermineMoreLoading(false);
            return;
        }
        void ladeTermineSeite(currentMailbox, null);
    }, [termineAktiv, currentMailbox, mailRefreshTick, ladeTermineSeite]);

    // Zähler am Umschalter: die UNGELESENEN unter den geladenen Termin-Mails.
    // Solange der Posteingang nicht zu Ende gesichtet ist (`hasMore`), ist das
    // eine Untergrenze — `terminZaehlerLabel` hängt dann ein „+" an, statt eine
    // glatte Zahl zu behaupten.
    const termineUnread = React.useMemo(() => ungeleseneTermine(termineMails), [termineMails]);
    const termineBadge = terminZaehlerLabel(termineUnread, !!termineMeta?.hasMore);

    // Der Filter selbst braucht weiterhin die Menge der verknüpften Schlüssel —
    // sie stammt jetzt aus derselben Quelle wie die Liste, ist also per
    // Konstruktion deckungsgleich mit ihr.
    const verknuepfteMails = React.useMemo<VerknuepfteMails | undefined>(() => {
        if (!nurVorgaenge) return undefined;
        return {
            messageIds: new Set(vorgangsMails.map((m) => m.id)),
            conversationIds: new Set(
                vorgangsMails.map((m) => m.conversationId).filter((c): c is string => !!c),
            ),
        };
    }, [nurVorgaenge, vorgangsMails]);
    const readableMailboxes = React.useMemo(
        () => mailboxes.filter(canReadMailbox).map((m) => m.mailboxAddress),
        [mailboxes],
    );
    // Outlook-Desktop-Darstellung: eigene Nachrichten tragen in der
    // aufgeklappten Unterhaltung den Hinweis „Gesendet". Dafür die Ids der
    // Gesendet-Ordner je Postfach — einmal aus dem ohnehin geladenen Baum.
    const gesendetIdsByMailbox = React.useMemo(() => {
        const out: Record<string, Set<string>> = {};
        for (const [mb, tree] of Object.entries(folderTrees)) out[mb] = gesendetOrdnerIds(tree);
        return out;
    }, [folderTrees]);
    // Mail-SP2: pro Postfach eine Lookup-Map Ordner-Id → Anzeigename (rekursiv
    // über den geladenen Ordnerbaum, Posteingang ausgelassen) für den Ordner-Chip
    // in den Suchtreffern. Nicht geladene Bäume fehlen schlicht → Chip entfällt.
    const folderNameByMailbox = React.useMemo(() => {
        const out: Record<string, Map<string, string>> = {};
        for (const [mb, tree] of Object.entries(folderTrees)) {
            out[mb] = buildFolderNameMap(tree);
        }
        return out;
    }, [folderTrees]);
    React.useEffect(() => {
        const q = searchQuery.trim();
        if (q.length < 2 || !currentMailbox) {
            setSearchResults(null);
            setSearching(false);
            return;
        }
        let cancelled = false;
        setSearching(true);
        const timer = window.setTimeout(async () => {
            try {
                const targets = resolveSearchTargets(readableMailboxes, currentMailbox);
                const perBox = await Promise.all(
                    targets.map(async (mb) => {
                        try {
                            const items = await graphMailService.searchMail(mb, q);
                            return items.map((e) => ({ ...e, srcMailbox: mb }) as SearchEmail);
                        } catch (err) {
                            reportMailError({
                                area: "mail",
                                action: "search",
                                error: err,
                                fallbackMessage: "Ein Postfach konnte nicht durchsucht werden.",
                            });
                            return [] as SearchEmail[]; // Postfach-Fehler isolieren
                        }
                    }),
                );
                if (cancelled) return;
                setSearchResults(perBox.flat().sort((a, b) => b.date.getTime() - a.date.getTime()));
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 350);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [searchQuery, currentMailbox, readableMailboxes]);
    // Postfach-/Ordnerwechsel schließt Lese-Panel + Suche (alter Kontext).
    React.useEffect(() => {
        setReadingEmail(null);
        setReadingMailbox(null);
        setSelectedEmailId("");
        setSearchQuery("");
        // SP2: neuer Ordner-/Postfach-Kontext startet ungefiltert.
        setMailFilter(EMPTY_MAIL_FILTER);
        // TERMINE (P6): Wer einen Ordner anklickt, will diesen Ordner sehen —
        // der Umschalter darf die Auswahl nicht überdauern. Der Ordner-Eintrag
        // „Termine" selbst bleibt davon unberührt: der hängt an `activeFolder`.
        setTermineOpen(false);
        // P7: und ungeklappt — die Konversationen des alten Ordners sind weg,
        // ihre nachgeladenen Nachrichten dürfen den neuen nicht überdauern.
        resetConversations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMailbox, activeFolder]);

    // P7 — Wechsel zwischen Ordnerliste und Suchtreffern: Suchtreffer sind
    // bewusst UNgruppiert (einzelne Mails), es gibt dort also nichts
    // aufzuklappen. Der Zustand wird beim Betreten UND beim Verlassen verworfen,
    // damit er nicht über die Suche hinweg auf fremde Zeilen zurückfällt.
    const searchAktiv = searchResults !== null;
    React.useEffect(() => {
        resetConversations();
    }, [searchAktiv, nurVorgaenge, einzeln, resetConversations]);

    // Vor dem (erneuten) Gruppieren Thread-Marker zurücksetzen — die Roh-Items
    // werden wiederverwendet und `groupConversations` mutiert das Haupt-Element.
    // T-29: in der Einzelansicht bleibt jede Mail eine eigene Zeile.
    const regroupUnread = (raw: Email[]): Email[] =>
        einzeln
            ? einzelneMails(raw)
            : groupConversations(raw.map((e) => ({ ...e, type: "normal" as Email["type"], thread: undefined })));

    // Deep-Link aus der globalen Suche: /emails?mailbox=…&message=… öffnet die
    // konkrete Nachricht (lädt das Email-Objekt nach). Parameter werden danach aus
    // der URL entfernt, damit Re-Render/Reload nicht erneut öffnet.
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkRef = React.useRef<string>("");
    React.useEffect(() => {
        const ziel = leseMailDeepLink(searchParams);
        if (!ziel) return;
        const mb = ziel.mailbox;
        const msg = ziel.messageId;
        const key = `${mb}|${msg}`;
        if (deepLinkRef.current === key) return;
        deepLinkRef.current = key;
        setCurrentMailbox(mb);
        let cancelled = false;
        (async () => {
            try {
                const email = await graphMailService.fetchMessageBody(mb, msg);
                if (cancelled) return;
                openMailDetailWindow(openWindow, {
                    email,
                    mailbox: mb,
                    onArchived: (id) => setEmails((prev) => prev.filter((e) => e.id !== id)),
                });
            } catch (err) {
                if (!cancelled) {
                    setActionError(reportMailError({
                        area: "mail",
                        action: "load-detail",
                        error: err,
                        fallbackMessage: "Die verlinkte Mail konnte nicht geöffnet werden.",
                    }));
                }
            } finally {
                setSearchParams(
                    (prev) => {
                        const next = new URLSearchParams(prev);
                        // Beide Namen räumen — sonst öffnete `msg=` beim nächsten
                        // Render erneut.
                        for (const name of MAIL_DEEPLINK_PARAMS) next.delete(name);
                        return next;
                    },
                    { replace: true },
                );
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [searchParams, setCurrentMailbox, openWindow, setSearchParams]);

    const activeFolderId = activeFolder?.id ?? null;
    // T-231: der Entwurfs-Bus-Hörer liest den Ordner über die Ref (kein Stale-Closure).
    activeFolderIdRef.current = activeFolderId;
    // T-179: „Mehr laden" prüft nach dem Abruf, ob die Liste noch dieselbe ist —
    // sonst hinge eine späte Seite aus „Gesendet" am inzwischen geöffneten Archiv.
    listenQuelleRef.current = `${currentMailbox ?? ""}|${activeFolderId ?? "inbox"}`;
    React.useEffect(() => {
        // Stale-Guard: bei schnellem Postfach-/Ordnerwechsel darf eine langsamere
        // ÄLTERE Response die neuere Liste nicht überschreiben (B3-Minimalfix).
        let cancelled = false;
        const fetchEmails = async () => {
            if (!currentMailbox) {
                setEmails([]);
                setUnreadMeta(null);
                return;
            }
            // TERMINE (P6): „termine" ist kein Graph-Ordner, sondern eine Sicht.
            // Ohne diesen Ausstieg liefe der Abruf unten mit `folder=termine`
            // und damit in einen Graph-404.
            // Die Liste kommt in diesem Fall aus `termineMails`.
            if (activeFolderId === TERMINE_FOLDER_KEY) {
                setEmails([]);
                setUnreadMeta(null);
                setLoadError(false);
                return;
            }
            setIsLoading(true);
            try {
                // Posteingang = 1:1 der Graph-Inbox (gelesene + ungelesene),
                // Archiv = echter Archiv-Ordner, sonst der Standard-Ordner bzw.
                // die echte Graph-Ordner-Id aus dem Baum (NICHT lowercasen, sie
                // ist bei Graph gross-/kleinschreibungsempfindlich).
                //
                // T-179 (28.10.2026): JEDER Ordner blättert jetzt seitenweise
                // wie der Posteingang. Vorher holten Gesendet, Archiv, Gelöscht,
                // Entwürfe und Unterordner fest die neuesten 100 Mails — in
                // „Gesendet" war das gut eine Woche, ältere waren unerreichbar.
                const res = await graphMailService.fetchInboxPaged(currentMailbox, activeFolderId);
                if (cancelled) return;
                unreadRawRef.current = res.emails;
                setUnreadMeta(res.meta);
                setHiddenTotal(res.meta.hiddenByRbac ?? 0);
                listenMailboxRef.current = currentMailbox;
                setEmails(regroupUnread(res.emails));
                setLoadError(false);
            } catch (err) {
                if (!cancelled) {
                    reportMailError({
                        area: "mail",
                        action: "load-list",
                        error: err,
                        fallbackMessage: "Mails konnten nicht geladen werden.",
                    });
                    // B4: sichtbar machen statt still alte Liste stehen lassen.
                    setLoadError(true);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        fetchEmails();
        // mailRefreshTick (PR5): bei Echtzeit-`mail`-Frame fürs aktuelle Postfach
        // neu laden — die Caches sind dann bereits verworfen → frische Liste.
        return () => {
            cancelled = true;
        };
    }, [activeFolderId, currentMailbox, mailRefreshTick, retryTick, einzeln]);

    // SP3/A3 — gemeinsame Sync-Routine (manueller Button UND On-Open-Auto-Sync):
    // Server-Delta anstoßen (pollMailboxNow), lokale Caches verwerfen, Liste + Baum
    // neu laden. Reentry-Guard per Ref, damit sich Auto-Sync und Button-Klick (bzw.
    // zwei schnelle Auto-Syncs) nicht überlappen und den Delta-Token doppelt ziehen.
    const syncingRef = React.useRef(false);
    const syncMailboxNow = React.useCallback(
        async (mailbox: string) => {
            if (syncingRef.current) return;
            syncingRef.current = true;
            setSyncing(true);
            try {
                await graphMailService.syncMailbox(mailbox);
                await refreshFolders(mailbox);
                setRetryTick((t) => t + 1); // Liste refetcht (Caches sind verworfen)
            } catch (err) {
                setActionError(reportMailError({
                    area: "mail",
                    action: "sync",
                    error: err,
                    fallbackMessage: "Das Postfach konnte nicht synchronisiert werden.",
                }));
            } finally {
                syncingRef.current = false;
                setSyncing(false);
            }
        },
        [refreshFolders],
    );

    // Manueller Sync (Topbar-Button „Synchronisieren").
    const handleSync = () => {
        if (!currentMailbox) return;
        void syncMailboxNow(currentMailbox);
    };

    // A3 — On-Open-Auto-Sync: beim Öffnen/Wechsel eines Postfachs ODER Ordners
    // automatisch synchronisieren (Server-Delta + Cache-Verwurf + Refetch), damit
    // gerade eingegangene ungelesene Mails ohne Zutun erscheinen — Webhook/Delta-
    // Poll allein reichten laut Review 2026-07-06 nicht. Kurze Debounce (Scheduler)
    // verhindert Doppel-Sync bei schnellem Durchklicken; der bestehende Gold-
    // Refresh-Balken (`syncing`) signalisiert den Lauf dezent.
    const syncFnRef = React.useRef(syncMailboxNow);
    React.useEffect(() => {
        syncFnRef.current = syncMailboxNow;
    }, [syncMailboxNow]);
    const onOpenSyncRef = React.useRef<OnOpenSyncScheduler | null>(null);
    if (onOpenSyncRef.current === null) {
        onOpenSyncRef.current = createOnOpenSyncScheduler({
            // Stets die aktuellste Sync-Routine über die Ref aufrufen (refreshFolders
            // ändert sich, sobald die Postfach-Liste lädt).
            sync: (mailbox) => void syncFnRef.current(mailbox),
        });
    }
    React.useEffect(() => {
        const scheduler = onOpenSyncRef.current;
        if (!scheduler || !currentMailbox) return;
        scheduler.schedule(currentMailbox);
        // Cleanup verwirft ein noch offenes Fenster → Wechsel A→B synchronisiert nur B.
        return () => scheduler.cancel();
        // activeFolderId in den Deps → auch ein Ordnerwechsel löst On-Open-Sync aus.
    }, [currentMailbox, activeFolderId]);

    // B10 — „Mehr laden": Folgeseite anhängen, Threads über alles neu gruppieren.
    // T-179: in jedem Ordner, nicht mehr nur im Posteingang.
    const handleLoadMore = async () => {
        if (!currentMailbox || !unreadMeta?.hasMore || loadingMore) return;
        const ordner = activeFolderId;
        const quelle = listenQuelleRef.current;
        setLoadingMore(true);
        try {
            const res = await graphMailService.fetchInboxPage(currentMailbox, unreadMeta.nextSkip, ordner);
            // Inzwischen Ordner oder Postfach gewechselt: die Seite gehört nicht
            // mehr zur sichtbaren Liste.
            if (listenQuelleRef.current !== quelle) return;
            unreadRawRef.current = [...unreadRawRef.current, ...res.emails];
            setUnreadMeta(res.meta);
            setHiddenTotal((h) => h + res.meta.hiddenByRbac);
            // P7: Hier wird bewusst NICHTS zugeklappt. Die Folgeseite bringt
            // ältere Mails, die `conversationId` überlebt das Neugruppieren, und
            // die aufgeklappten Nachrichten kommen ohnehin aus
            // `fetchConversation` (der vollständigen Konversation) — nicht aus
            // der geladenen Seite. Zuklappen würde dem Nutzer nur seine
            // geöffneten Threads wegnehmen.
            setEmails(regroupUnread(unreadRawRef.current));
        } catch (err) {
            setActionError(reportMailError({
                area: "mail",
                action: "load-more",
                error: err,
                fallbackMessage: "Weitere Mails konnten nicht geladen werden.",
            }));
        } finally {
            setLoadingMore(false);
        }
    };
    // „Wir stehen im Posteingang" — DIE Aussage, an der das Kontextmenü hängt:
    // „In Posteingang verschieben" wäre dort ein No-Op-Graph-Move auf denselben
    // Ordner. TERMINE (P6) ändert daran nichts: die Termin-Mails LIEGEN im
    // Posteingang, die Sicht zeigt sie nur ein zweites Mal.
    const isUnreadView = !activeFolderId || activeFolderId === "inbox";
    // Getrennt davon die Frage, wer die Nachlade-Zeile besitzt: Die Termine-Sicht
    // bringt ihre eigene Paginierung mit, die Ordner-Zeile „Mehr laden" darf
    // darunter nicht zusätzlich stehen. T-179: sonst steht sie in JEDEM Ordner.
    const zeigeOrdnerNachladen = !termineAktiv;

    // SP2: aktive Suche ersetzt die Ordner-Liste durch die Treffer.
    //
    // L1: Ist der Filter „Vorgänge" an, ist die Quelle weder Ordner noch Suche,
    // sondern die verknüpften Mails des Postfachs. Die globale Server-Suche
    // kann dort nicht greifen (sie sucht über Postfächer, nicht über
    // Verknüpfungen) — statt die Eingabe wirkungslos verpuffen zu lassen, wird
    // ihr Text lokal über Betreff/Absender/Vorschau/Vorgangstitel angewandt.
    const baseEmails = React.useMemo<SearchEmail[]>(
        () =>
            nurVorgaenge
                ? vorgangsMails.filter((m) => matchesVorgangsMailSuche(m, searchQuery))
                : termineAktiv
                    // TERMINE (P6): Wie beim Vorgangs-Filter kann die globale
                    // Server-Suche diese Sicht nicht durchsuchen — sie sucht
                    // Postfächer, nicht Sichten. Statt die Eingabe verpuffen zu
                    // lassen, wird sie lokal über Betreff/Absender/Vorschau und
                    // die Termin-Art angewandt.
                    ? termineMails.filter((m) => matchesTerminSuche(m, searchQuery))
                    : (searchResults ?? emails),
        [nurVorgaenge, vorgangsMails, termineAktiv, termineMails, searchQuery, searchResults, emails],
    );
    const searchActive = !nurVorgaenge && !termineAktiv && searchResults !== null;

    // ── P7 (PO 14.09.2026): Vorgangs-Index — „gehört diese Mail zu einem Vorgang?" ──
    //
    // Bis heute beantwortete die Mail-App das nirgends: der Filter „Vorgänge"
    // zeigt Snapshot-Zeilen aus der Verknüpfungstabelle, aber welche der
    // Posteingangs-Mails an einem Vorgang hängen, sah man nicht — auch nicht im
    // Lesefenster (State A gilt nur für noch NICHT gebundene Mails). Einen
    // Marker je Zeile gab es nie; die Sieb-Form des Filters (a14fdfca, 23.08.)
    // war das Nächste und wurde vor dem Merge bewusst ersetzt.
    //
    // Jetzt: EIN Batch-Lookup je sichtbarer Seite (`POST /mail/links/lookup`),
    // nur für die noch unbekannten Konversationen — „Mehr laden" fragt also nur
    // das Delta nach. Eigener Effekt wie beim Erwähnungs-Index: die Liste
    // rendert sofort, der Marker trifft später ein. Kein Graph-Aufruf, also
    // keine Exchange-Verbindungsgrenze wie beim Thread-Index — der Abruf darf
    // parallel zum Listenladen laufen.
    //
    // Schlüssel ist (Postfach, conversationId): Suchtreffer kommen aus fremden
    // Postfächern, und der Server prüft je Postfach (`assertMailRead`) — ein
    // Postfach ohne Leserecht bleibt schlicht ohne Marker.
    //
    // Snapshot-Zeilen (Filter „Vorgänge") werden NICHT nachgefragt: sie tragen
    // ihren Vorgang aus dem Snapshot mit (`vorgangsMarker` liest ihn direkt).
    const vorgangsIndex = useMailVorgangsIndex({
        emails: baseEmails,
        currentMailbox,
        resetKey: mailRefreshTick,
        // Stale-Guard: `emails` gehört nach einem Postfachwechsel kurz noch
        // zum vorigen Postfach. Suche/Termine tragen ihre Quelle selbst.
        enabled:
            !nurVorgaenge &&
            !!currentMailbox &&
            (searchActive || termineAktiv || listenMailboxRef.current === currentMailbox),
    });

    // SP2(Filterleiste): Absender/Empfänger + Zeitraum clientseitig anwenden
    // (kombinierbar mit der globalen Suche). Ungefiltert = identische Referenz.
    const filterActive = isMailFilterActive(mailFilter);
    const currentEmails = React.useMemo(
        () => applyMailFilter(baseEmails, mailFilter, now, verknuepfteMails),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [baseEmails, mailFilter, verknuepfteMails],
    );
    // SP1/SP2: Panel liest gegen das Quell-Postfach des Treffers (Fallback: aktuelles).
    const paneMailbox = readingMailbox ?? currentMailbox;

    // Group emails by date
    const groupedEmails = useMemo(() => {
        let groups: { label: string; emails: Email[] }[] = [];
        const groupMap = new Map<string, Email[]>();

        for (const email of currentEmails) {
            const group = getDateGroup(email.date, now);
            if (!groupMap.has(group)) {
                groupMap.set(group, []);
            }
            groupMap.get(group)!.push(email);
        }

        groupMap.forEach((emls, label) => {
            groups.push({ label, emails: emls });
        });

        return groups;
    }, [currentEmails]);

    const renderTypeIcon = (type: string) => {
        if (type === "conversation") return <Chat20Regular />;
        if (type === "brief") return <Mail20Regular />;
        return null;
    };

    /**
     * J2 — eine Zuweisung ist gefallen: den erkannten Kontakt der betroffenen
     * Zeilen sofort nachziehen, damit die Liste nicht weiter den alten Namen
     * zeigt. Rein optimistisch, kein Refetch; die Wahrheit steht im
     * persistierten MailLink und kommt beim nächsten Laden ohnehin mit.
     *
     * P7 (PO 14.09.2026): meldet der Bus eine VORGANGS-Verknüpfung, wird der
     * Vorgangsbezug dieser Konversation frisch geholt — nicht optimistisch
     * gesetzt, denn die Meldung kennt weder Titel noch Sichtbarkeit. Ein
     * einzelner Lookup, und der Marker steht, ohne die Liste neu zu laden.
     */
    React.useEffect(
        () =>
            abonniereZuweisung((z) => {
                if (z.kontakt !== undefined) {
                    const kontakt = z.kontakt;
                    const passt = (e: Email) =>
                        betrifft(z, {
                            mailbox: readingMailbox ?? currentMailbox ?? "",
                            messageId: e.id,
                            conversationId: e.conversationId ?? null,
                        });
                    const patch = <T extends Email>(e: T): T =>
                        passt(e) ? ({ ...e, recognizedContact: kontakt, matchedContact: kontakt } as T) : e;
                    setEmails((prev) => prev.map(patch));
                    setSearchResults((prev) => (prev ? prev.map(patch) : prev));
                    setVorgangsMails((prev) => prev.map(patch));
                    setTermineMails((prev) => prev.map(patch));
                    setReadingEmail((cur) => (cur ? patch(cur) : cur));
                }
            }),
        [currentMailbox, readingMailbox],
    );

    // Aus Liste/Suchtreffern UND Lese-Panel entfernen (nach Archivieren/Zuweisen).
    const removeFromList = (id: string) => {
        // T-179: auch aus den Roh-Seiten — „Mehr laden" baut die Liste daraus
        // neu auf, und das geht jetzt in jedem Ordner. Sonst stünde eine gerade
        // verschobene Mail nach dem Nachladen wieder da.
        unreadRawRef.current = unreadRawRef.current.filter((e) => e.id !== id);
        setEmails((prev) => prev.filter((e) => e.id !== id));
        setSearchResults((prev) => (prev ? prev.filter((e) => e.id !== id) : prev));
        // L1: auch aus der Vorgangs-Quelle, sonst bliebe die Zeile im Filter
        // stehen, obwohl die Mail archiviert/gelöscht wurde.
        setVorgangsMails((prev) => prev.filter((e) => e.id !== id));
        // TERMINE (P6): und aus der Termine-Sicht — eine archivierte Zusage darf
        // dort nicht stehen bleiben.
        setTermineMails((prev) => prev.filter((e) => e.id !== id));
        // P7: auch aus den aufgeklappten Konversationen — eine archivierte
        // Nachricht darf nicht eingerückt weiterleben.
        setConvChildren((prev) => {
            let geaendert = false;
            const next: Record<string, Email[]> = {};
            for (const [convId, kinder] of Object.entries(prev)) {
                const ohne = kinder.filter((e) => e.id !== id);
                if (ohne.length !== kinder.length) geaendert = true;
                next[convId] = ohne;
            }
            return geaendert ? next : prev;
        });
        setReadingEmail((cur) => (cur?.id === id ? null : cur));
    };

    /**
     * P7 — eine Konversation auf-/zuklappen. Der Zustand kippt sofort; die
     * Nachrichten kommen aus `fetchConversation` (der VOLLSTÄNDIGEN Konversation
     * laut Graph, nicht nur dem, was gerade in der Liste steht) und werden je
     * Konversation einmal geholt und gemerkt.
     */
    const toggleConversation = (email: SearchEmail) => {
        const convId = email.conversationId;
        const wirdGeoeffnet = !expandedConvs.has(convId);
        setExpandedConvs((prev) => schalteAufklappen(prev, convId));
        if (!wirdGeoeffnet) return;
        void ladeKonversation(email);
    };

    const ladeKonversation = (email: SearchEmail) => {
        const convId = email.conversationId;
        if (convChildren[convId] || convLoading.has(convId)) return;
        const mb = email.srcMailbox ?? currentMailbox;
        if (!mb) return;
        setConvError((prev) => {
            if (!prev.has(convId)) return prev;
            const next = new Set(prev);
            next.delete(convId);
            return next;
        });
        setConvLoading((prev) => new Set(prev).add(convId));
        void graphMailService
            .fetchConversation(mb, convId)
            .then((alle) =>
                setConvChildren((prev) => ({ ...prev, [convId]: konversationsZeilen(alle, email) })),
            )
            .catch((err: unknown) => {
                reportMailError({
                    area: "mail",
                    action: "load-conversation",
                    error: err,
                    fallbackMessage: "Die Konversation konnte nicht geladen werden.",
                });
                setConvError((prev) => new Set(prev).add(convId));
            })
            .finally(() =>
                setConvLoading((prev) => {
                    const next = new Set(prev);
                    next.delete(convId);
                    return next;
                }),
            );
    };

    /**
     * P9 — gelesen/ungelesen umschalten. EIN Weg für den Punkt an der Zeile und
     * den Menüpunkt im Kontextmenü: erst optimistisch umschalten (die Liste soll
     * dem Klick nicht hinterherhinken), bei Fehler exakt zurückrollen.
     *
     * Nach Erfolg wird der Ordnerbaum nachgezogen: die Ungelesen-Zahl am Ordner
     * ist Graphs `unreadItemCount` und kommt ausschließlich aus
     * `GET /mail/folders` — ohne den Refresh zeigte die Sidebar die alte Zahl,
     * bis irgendwann ein Sync läuft.
     *
     * Die Argumentreihenfolge ist `(messageId, mailbox)`; sie stand hier einmal
     * vertauscht, und weil beides `string` ist, fiel es TypeScript nicht auf.
     */
    const toggleRead = (email: Email, mailbox: string, read: boolean): Promise<void> => {
        setActionError(null);
        const flip = (val: boolean) => {
            // Die Roh-Seiten des Posteingangs MUESSEN mitlaufen: „Mehr laden"
            // baut die Liste aus `unreadRawRef` neu auf (regroupUnread). Blieben
            // sie stehen, spraenge die gerade umgeschaltete Mail beim naechsten
            // Nachladen sichtbar in ihren alten Lesestatus zurueck — obwohl der
            // Server laengst den neuen hat.
            unreadRawRef.current = setzeLesestatus(unreadRawRef.current, email.id, val);
            setEmails((prev) => setzeLesestatus(prev, email.id, val));
            setSearchResults((prev) => (prev ? setzeLesestatus(prev, email.id, val) : prev));
            setVorgangsMails((prev) => setzeLesestatus(prev, email.id, val));
            // TERMINE (P6): dieselbe Mail steht ggf. auch in der Termine-Sicht —
            // sonst hinge dort der alte Lesestatus, inklusive falschem Zähler.
            setTermineMails((prev) => setzeLesestatus(prev, email.id, val));
            setConvChildren((prev) => {
                let geaendert = false;
                const next: Record<string, Email[]> = {};
                for (const [convId, kinder] of Object.entries(prev)) {
                    const neu = setzeLesestatus(kinder, email.id, val);
                    if (neu !== kinder) geaendert = true;
                    next[convId] = neu;
                }
                return geaendert ? next : prev;
            });
            setReadingEmail((cur) =>
                cur && cur.id === email.id && cur.read !== val ? { ...cur, read: val } : cur,
            );
        };
        flip(read);
        const call = read
            ? graphMailService.markRead(email.id, mailbox)
            : graphMailService.markAsUnread(email.id, mailbox);
        return call
            .then(() => void refreshFolders(mailbox))
            .catch((err: unknown) => {
                flip(!read); // bei Fehler zurückrollen
                setActionError(reportMailError({
                    area: "mail",
                    action: read ? "mark-read" : "mark-unread",
                    error: err,
                    fallbackMessage: "Der Lesestatus konnte nicht geändert werden.",
                }));
            });
    };

    // ── Kontextmenü-Aktionen — operieren auf der Mail des offenen Menüs ──
    /** Eine Mail im selben Postfach verschieben — Kontextmenü UND Auswahlmodus. */
    const verschiebeMail = (mailbox: string, email: Email, destinationId: string, label: string): Promise<void> => {
        setActionError(null);
        return graphMailService
            .moveMessage(mailbox, email.id, destinationId)
            .then(() => {
                removeFromList(email.id);
                void refreshFolders(mailbox);
            })
            .catch((err: unknown) => setActionError(reportMailError({
                area: "mail",
                action: "move",
                error: err,
                fallbackMessage: `${label} fehlgeschlagen.`,
            })));
    };
    const doMove = (destinationId: string, label: string) => {
        const m = ctxMenu;
        if (!m) return;
        void verschiebeMail(m.mailbox, m.email, destinationId, label);
    };
    /**
     * Cluster POSTFACH (E02-Zusatz) — Ziel aus dem Verschieben-Untermenü.
     *
     * Im selben Postfach bleibt es der gewohnte Graph-Move. Über Postfachgrenzen
     * hinweg gibt es keinen Move: der Server kopiert das MIME ins Ziel und legt
     * das Original in den Papierkorb. Beide Ordnerbäume ändern sich, und wenn nur
     * die Kopie gelang, wird das benannt statt als Erfolg verbucht.
     */
    const doMoveTo = (t: MoveTarget) => {
        const m = ctxMenu;
        if (!m) return;
        if (!t.crossMailbox) {
            doMove(t.folderKey, "Verschieben");
            return;
        }
        setActionError(null);
        void graphMailService
            .moveMessageToMailbox(m.mailbox, m.email.id, t.mailbox, t.folderKey)
            .then((res) => {
                removeFromList(m.email.id);
                void refreshFolders(m.mailbox);
                void refreshFolders(t.mailbox);
                setActionNote(
                    res.sourceRemoved
                        ? `Nach ${moveTargetLabel(t)} verschoben.`
                        : `Kopie in ${moveTargetLabel(t)} angelegt — das Original liegt noch im Quell-Postfach.`,
                );
            })
            .catch((err: unknown) => setActionError(reportMailError({
                area: "mail",
                action: "move",
                error: err,
                fallbackMessage: "Die Mail konnte nicht verschoben werden.",
            })));
    };

    /** Eine Mail archivieren — Kontextmenü UND Auswahlmodus. */
    const archiviereMail = (mailbox: string, email: Email): Promise<void> => {
        setActionError(null);
        return graphMailService
            .archiveMessage(mailbox, email.id)
            .then(() => {
                removeFromList(email.id);
                void refreshFolders(mailbox);
            })
            .catch((err: unknown) => setActionError(reportMailError({
                area: "mail",
                action: "archive",
                error: err,
                fallbackMessage: "Die Mail konnte nicht archiviert werden.",
            })));
    };
    const doArchive = () => {
        const m = ctxMenu;
        if (!m) return;
        void archiviereMail(m.mailbox, m.email);
    };
    /**
     * Löschen = Verschieben nach `deleteditems`. Genau wie „Archivieren" ein
     * Verschieben nach `archive` ist — es gibt bewusst kein Hard-Delete, die
     * Mail bleibt im Papierkorb wiederherstellbar.
     */
    const deleteEmail = (mailbox: string, email: Email): Promise<void> => {
        setActionError(null);
        const from = activeFolderId ?? "inbox";
        const entwurf = istEntwurfsLoeschen({
            activeFolderId,
            srcMailbox: (email as SearchEmail).srcMailbox,
            darfSenden: mailboxes.some(
                (m) => m.mailboxAddress.toLowerCase() === mailbox.toLowerCase() && canSendMailbox(m),
            ),
        });
        return loescheMailZeile(
            { mailbox, id: email.id, entwurf },
            { moveMessage: graphMailService.moveMessage, deleteDraft: graphMailService.deleteDraft },
        )
            .then((ergebnis) => {
                removeFromList(email.id);
                void refreshFolders(mailbox);
                if (ergebnis.art === "schon-weg") {
                    // T-231: Die Zeile war veraltet — Liste frisch holen und ehrlich sagen,
                    // was los ist (kein „gelöscht", kein Rückgängig ins Leere).
                    setRetryTick((t) => t + 1);
                    setActionError(MAIL_SCHON_WEG_TEXT);
                    return;
                }
                // Ohne neue Id kein Rückweg — dann lieber gar keinen
                // Rückgängig-Knopf anbieten als einen, der ins Leere läuft.
                if (ergebnis.art === "papierkorb" && ergebnis.neueId) {
                    setUndoDelete({ mailbox, newId: ergebnis.neueId, from, subject: email.subject || "(kein Betreff)" });
                }
            })
            .catch((err: unknown) => setActionError(reportMailError({
                area: "mail",
                action: "delete",
                error: err,
                fallbackMessage: "Die Mail konnte nicht in den Papierkorb verschoben werden.",
            })));
    };
    const doDelete = () => {
        const m = ctxMenu;
        if (!m) return;
        deleteEmail(m.mailbox, m.email);
    };
    const doUndoDelete = () => {
        const u = undoDelete;
        if (!u) return;
        setUndoDelete(null);
        setActionError(null);
        void graphMailService
            .moveMessage(u.mailbox, u.newId, u.from)
            .then(() => {
                void refreshFolders(u.mailbox);
                setRetryTick((t) => t + 1); // Liste neu laden — die Mail ist zurück
            })
            .catch((err: unknown) => setActionError(reportMailError({
                area: "mail",
                action: "restore",
                error: err,
                fallbackMessage: "Die Mail konnte nicht wiederhergestellt werden.",
            })));
    };

    /**
     * G4 — „Regel erstellen…" aus dem Kontextmenü der Liste. Öffnet dasselbe
     * Fenster wie Vorgang und Lesefenster, vorbelegt aus Absender, Betreff und
     * Postfach der angeklickten Mail. Weil `renderCtxMenu()` von allen drei
     * Ansichten benutzt wird, gilt der Eintrag automatisch für Phone, Tablet
     * und Desktop.
     */
    const doCreateRule = () => {
        const m = ctxMenu;
        if (!m) return;
        openMailRegelWindow(openWindow, {
            vorbelegung: {
                senderEmail: m.email.senderEmail,
                subject: m.email.subject,
                mailbox: m.mailbox,
            },
        });
    };

    // Menüpunkt „Als gelesen/ungelesen markieren" — derselbe Weg wie der
    // Schalter an der Zeile (P9), damit beide dieselbe Wahrheit schreiben.
    const doToggleRead = () => {
        const m = ctxMenu;
        if (!m) return;
        toggleRead(m.email, m.mailbox, !m.email.read);
    };

    // ── Cluster POSTFACH (G1/G2/G3): die drei Bearbeitungs-Wege aus der Liste ──
    // Der Ablauf selbst liegt in `mailListActions.ts` (ohne JSX, damit er ohne
    // Fenster prüfbar bleibt); hier wird nur der Kontext gebaut und der
    // Beschäftigt-Zustand der Zeile verwaltet.
    const starteListenAktion = (
        lauf: (ctx: MailListActionCtx) => Promise<void>,
    ): void => {
        const m = ctxMenu;
        if (!m) return;
        // Läuft für DIESELBE Mail schon etwas, wird nicht erneut gestartet. Das
        // Menü schließt über `run()` sofort, kann den Doppelklick also nicht
        // selbst verhindern.
        if (pendingAction === m.email.id) return;
        setActionError(null);
        setPendingAction(m.email.id);
        const ctx: MailListActionCtx = {
            email: m.email,
            mailbox: m.mailbox,
            openWindow,
            onDone: removeFromList,
            onRefreshFolders: (mailbox) => void refreshFolders(mailbox),
            onError: setActionError,
            onNote: setActionNote,
            navigateToVorgang: (vorgangId) => navigate(`/vorgaenge/${vorgangId}`),
            navigateToSchadenOrdner: (contactId, ordner) => navigate(schadenOrdnerLink(contactId, ordner)),
            // Entscheidet beim Zuweisen, welche Nachrichten des Verlaufs mit
            // archiviert werden dürfen (Ordnerbaum aus dem MailContext).
            istImPosteingang: posteingangsPruefung(folderTrees, m.mailbox),
            mitgenommeneNennen: einzeln,
            schadenAblage: mailboxes.some(
                (mb) => mb.schadenAblage === true && mb.mailboxAddress.toLowerCase() === m.mailbox.toLowerCase(),
            ),
        };
        void lauf(ctx)
            .catch((err: unknown) => setActionError(reportMailError({
                area: "mail",
                action: "assign",
                error: err,
                fallbackMessage: "Die Mail-Aktion konnte nicht abgeschlossen werden.",
            })))
            // Fällt die Zeile zwischenzeitlich über einen Echtzeit-Frame aus der
            // Liste, läuft die begonnene Aktion zu Ende — die Fenster hängen am
            // WindowManager, nicht an der Zeile.
            .finally(() => setPendingAction((cur) => (cur === m.email.id ? null : cur)));
    };

    // SP8: Klick auf eine Erwähnung → Mail als Fenster öffnen (lädt das Email-
    // Objekt am Quell-Postfach nach; funktioniert auch für fremde, LESBARE
    // Postfächer) und die Erwähnung optimistisch als gelesen markieren.
    const openMentionMail = async (m: MailMentionItemDto) => {
        if (!m.read) {
            setMentions((prev) => prev.map((x) => (x.noteId === m.noteId ? { ...x, read: true } : x)));
            mailNoteService.markMentionRead(m.noteId).catch((err) => reportMailError({
                area: "mail",
                action: "mark-read",
                error: err,
                fallbackMessage: "Die Erwähnung konnte nicht als gelesen markiert werden.",
            }));
        }
        try {
            const email = await graphMailService.fetchMessageBody(m.mailbox, m.messageId);
            openMailDetailWindow(openWindow, {
                email,
                mailbox: m.mailbox,
                onArchived: removeFromList,
            });
        } catch (err) {
            setActionError(reportMailError({
                area: "mail",
                action: "load-detail",
                error: err,
                fallbackMessage: "Die Mail zur Erwähnung konnte nicht geöffnet werden.",
            }));
        }
    };

    /**
     * L1 — eine Zeile aus dem Vorgangs-Filter öffnen. Sie stammt aus einem
     * Verknüpfungs-Snapshot und hat keinen Rumpf; der wird am Quell-Postfach
     * nachgeladen (dasselbe Muster wie `openMentionMail`). Ohne diesen Umweg
     * stünde im Lesebereich eine leere Mail.
     */
    const oeffneSnapshotMail = async (email: SearchEmail, mailbox: string) => {
        setActionError(null);
        try {
            const voll = await graphMailService.fetchMessageBody(mailbox, email.id);
            // Desktop im Lesebereich, Handy/Tablet als Vollansicht über der Liste.
            setReadingEinzeln(false);
            setReadingEmail(voll);
            setReadingMailbox(mailbox);
        } catch (err) {
            setActionError(reportMailError({
                area: "mail",
                action: "load-detail",
                error: err,
                fallbackMessage:
                    "Diese Mail konnte nicht geöffnet werden — vielleicht wurde sie verschoben oder gelöscht.",
            }));
        }
    };

    /**
     * `ganzeUnterhaltung`: die Zeile ist die KOPFZEILE einer Unterhaltung —
     * dann zeigt der Lesebereich alle Nachrichten (neueste oben, durch Linien
     * getrennt), wie Outlook Desktop beim Klick auf die Unterhaltung. Eine
     * Nachricht darunter (oder eine Mail ohne Unterhaltung) zeigt nur sich.
     */
    const openMail = (email: SearchEmail, ganzeUnterhaltung = false) => {
        setSelectedEmailId(email.id);
        setUnterhaltungGewaehlt(ganzeUnterhaltung);
        // SP2: Treffer aus „alle Postfächer" öffnen gegen ihr Quell-Postfach.
        const mailboxFor = email.srcMailbox ?? currentMailbox;
        if (!mailboxFor) return;
        // L1: Snapshot-Zeile → erst den Rumpf holen, dann anzeigen.
        if (email.snapshotOnly) {
            void oeffneSnapshotMail(email, mailboxFor);
            return;
        }
        // T-212: Im Ordner „Entwürfe" öffnet ein Klick das Mailfenster zum
        // Weiterschreiben — wie in Outlook. Nur mit Senderecht; wer das Postfach
        // bloß lesen darf, bekommt weiter die Lese-Ansicht.
        if (
            activeFolderId === "drafts" &&
            !email.srcMailbox &&
            mailboxes.some((m) => m.mailboxAddress.toLowerCase() === mailboxFor.toLowerCase() && canSendMailbox(m))
        ) {
            openEntwurfWindow(openWindow, { mailbox: mailboxFor, draftId: email.id, titel: email.subject });
            return;
        }
        // Outlook Desktop: eine Nachricht der Unterhaltung liest sich einzeln —
        // sie verliert dafür Verlauf und Thread-Anhänge der Kopfzeile. Die
        // Kopfzeile selbst steht für die ganze Unterhaltung.
        const nachricht = ganzeUnterhaltung ? email : alsEinzelnachricht(email);
        // SP1: Desktop liest im rechten Panel; Handy/Tablet zeigen dieselbe
        // Lese-Ansicht als Vollansicht über der Liste (mit „Zurück").
        setReadingEinzeln(!ganzeUnterhaltung);
        setReadingEmail(nachricht);
        setReadingMailbox(mailboxFor);
    };

    // Im Ordner „Gesendet" stehen die gezählten Antworten selbst in der Liste —
    // der Thread-Index würde sie ein zweites Mal addieren.
    const imGesendetOrdner = activeFolderId === "sentitems";
    // T-193: In „Gesendet" und „Entwürfe" nennt die Zeile die Empfänger — der
    // Absender ist dort immer man selbst. Nicht bei einer Suche: deren
    // Treffer kommen aus allen Ordnern.
    const empfaengerZeigen = !searchActive && (imGesendetOrdner || activeFolderId === "drafts");

    /**
     * Eine Zeile der Mailliste. Ausgelagert, weil eine aufgeklappte Konversation
     * (P7) dieselbe Zeile eingerückt noch einmal braucht — zwei Kopien desselben
     * JSX wären zwei Orte, an denen jede künftige Änderung passieren müsste.
     *
     * `kind = true` markiert eine Nachricht INNERHALB einer aufgeklappten
     * Konversation: sie rückt ein und trägt selbst keinen Aufklapp-Schalter.
     */
    const renderMailRow = (email: SearchEmail, touch: boolean, kind: boolean) => {
        const zeilenMailbox = email.srcMailbox ?? currentMailbox;
        const convId = email.conversationId;
        const offen = expandedConvs.has(convId);
        // Der Thread-Index zählt die eigenen Antworten aus „Gesendet" — die
        // liegen nicht im geladenen Ordner und fehlten der Faltung deshalb
        // komplett. Im Ordner „Gesendet" selbst wäre das eine Doppelzählung.
        const extern = imGesendetOrdner ? 0 : externeAnzahl(email, threadIndex[convId]);
        const aufklappbar = !kind && !einzeln && istAufklappbar(email, extern);
        const anzahl = sichtbareAnzahl(email, offen ? convChildren[convId] : undefined, extern);
        const erwaehnung = erwaehnungsMarker(mentionIndex, zeilenMailbox, email.id);
        // P7 (PO 14.09.2026): gehört die Konversation zu einem Vorgang?
        const vorgaengeDerZeile = vorgangsMarker(vorgangsIndex, zeilenMailbox, email);
        // T-212 (Teil B): Der Index gilt nur für das aktuelle Postfach.
        const entwurfDerZeile =
            activeFolderId !== "drafts" && !email.srcMailbox && convId ? entwurfIndex[convId] : undefined;
        const leseLabel = leseSchalterLabel(email.read);
        const person = zeilenPerson(email, empfaengerZeigen);
        // Outlook Desktop: eigene Nachrichten in der aufgeklappten Unterhaltung
        // tragen „Gesendet" — auch im Ordner „Gesendet", denn die Unterhaltung
        // darunter mischt gesendete und empfangene Nachrichten.
        const gesendet =
            kind &&
            istGesendet(email, zeilenMailbox, (zeilenMailbox && gesendetIdsByMailbox[zeilenMailbox]) || KEINE_ORDNER);
        // Die neueste Nachricht steht aufgeklappt zweimal da: als Kopfzeile und
        // als erste Nachricht darunter. Markiert wird dann nur die Nachricht —
        // wie in Outlook Desktop, wo die Kopfzeile für die Unterhaltung steht.
        //
        // Wurde dagegen die Kopfzeile selbst angeklickt (ganze Unterhaltung im
        // Lesebereich), steht die Markierung an der Kopfzeile.
        const ausgewaehlt =
            selectedEmailId === email.id &&
            (kind
                ? !unterhaltungGewaehlt
                : unterhaltungGewaehlt || !(offen && (convChildren[convId] ?? []).some((k) => k.id === email.id)));
        // Handy: Auswahlmodus (langer Druck) — Antippen wählt statt zu öffnen.
        const imAuswahlModus = isPhone && auswahl !== null;
        const markiert = imAuswahlModus && auswahl.has(email.id);
        const waehleZeile = () => {
            if (!zeilenMailbox) return;
            setAuswahl((a) => schalteAuswahl(a ?? new Map(), { email, mailbox: zeilenMailbox }));
        };
        return (
            <SwipeToDeleteRow
                // Kopfzeile und erste Nachricht darunter teilen sich die Id.
                key={kind ? `nachricht-${email.id}` : email.id}
                // Nur auf dem Handy und nicht im Papierkorb: dort
                // waere das Loeschen ein No-Op. Auf dem Desktop
                // fuehrt das Kontextmenue zum selben Ziel.
                enabled={isPhone && !imAuswahlModus && activeFolderId !== "deleteditems"}
                onDelete={() => {
                    if (zeilenMailbox) deleteEmail(zeilenMailbox, email);
                }}
            >
                <div
                    className={
                        mergeClasses(
                            styles.mailItem,
                            touch && styles.mailItemTouch,
                            kind && styles.mailItemChild,
                            (imAuswahlModus ? markiert : ausgewaehlt) && styles.mailItemSelected,
                        ) + (isPhone ? " mm-zeile-touch" : "")
                    }
                    aria-selected={imAuswahlModus ? markiert : undefined}
                    // Sichtbare Spanne zwischen Klick im Kontextmenü
                    // und geöffnetem Fenster (Erkennung + Anhänge).
                    aria-busy={pendingAction === email.id || undefined}
                    style={pendingAction === email.id ? { opacity: 0.55 } : undefined}
                    onClick={() => {
                        // Der lange Druck hat schon ausgewählt — kein Öffnen hinterher.
                        if (langerDruck.klickSchlucken()) return;
                        if (imAuswahlModus) {
                            waehleZeile();
                            return;
                        }
                        // Kopfzeile einer Unterhaltung → die ganze Unterhaltung lesen.
                        openMail(email, aufklappbar);
                        // Outlook Desktop: ein Klick auf die Kopfzeile klappt die
                        // Unterhaltung auf (zu klappt nur der Pfeil). Handy/Tablet
                        // öffnen die Vollansicht darüber — die Liste bleibt, wie sie war.
                        if (usePane && aufklappbar && !offen) toggleConversation(email);
                    }}
                    // Handy: langer Druck startet den Auswahlmodus (Outlook iOS).
                    {...(isPhone && zeilenMailbox ? langerDruck.fuer(waehleZeile) : {})}
                    // User 01.11.2026: die Mail in einen Vorgang oder in die
                    // Dokumente eines Kontakts ziehen — auch in ein anderes
                    // Browserfenster. Maßgeblich ist der Zeiger, nicht das
                    // Layout: Ein Edge-Fenster auf halber Bildschirmbreite
                    // zeigt das Tablet-/Handy-Layout und muss trotzdem ziehen.
                    {...mailZeileZiehbar(zeilenMailbox, email, feinerZeiger)}
                    onContextMenu={(e) => {
                        if (!zeilenMailbox) return;
                        e.preventDefault();
                        // Handy: der lange Druck (Android meldet ihn auch als
                        // `contextmenu`) gehört dem Auswahlmodus; die Aktionen
                        // stehen dann in der Leiste unten.
                        if (isPhone) return;
                        setCtxMenu({ x: e.clientX, y: e.clientY, email, mailbox: zeilenMailbox });
                    }}
                >
                    {/* P9 — Lesestatus umschalten. Der Klick darf die Zeile NICHT
                        öffnen, sonst schlüge jedes Zurückstellen sofort wieder um.

                        NICHT im Vorgangs-Filter: dessen Zeilen sind Snapshots aus
                        der Verknüpfungs-Tabelle, und die kennt den Gelesen-Status
                        nicht — `snapshotZuEmail` setzt `read: true` als ehrlichen
                        Platzhalter (services/vorgangsMails.ts:52). Ein Schalter
                        darauf zeigte einen erfundenen Zustand an und schriebe beim
                        Klick einen, den niemand geprüft hat. */}
                    {!imAuswahlModus && !(email as { snapshotOnly?: boolean }).snapshotOnly && (
                    <button
                        type="button"
                        className={mergeClasses(styles.readToggle, kind && styles.readToggleChild)}
                        aria-label={leseLabel}
                        title={leseLabel}
                        disabled={!zeilenMailbox}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (zeilenMailbox) toggleRead(email, zeilenMailbox, !email.read);
                        }}
                    >
                        <span className={email.read ? styles.readRing : styles.unreadDot} />
                    </button>
                    )}

                    {/* Auswahlmodus (Handy): der Kreis steht, wo sonst das Bild steht. */}
                    {imAuswahlModus && (
                        <div style={{ display: "flex", alignItems: "center", alignSelf: "center", flexShrink: 0 }}>
                            <AuswahlKreis an={markiert} />
                        </div>
                    )}
                    {/* Outlook Desktop: die Nachrichten unter der Kopfzeile stehen
                        ohne Bild, eingerückt unter dem Namen der Kopfzeile. */}
                    {!kind && !imAuswahlModus && (
                    <div className={styles.mailItemLeft}>
                        <div className={styles.typeIcon}>{renderTypeIcon(email.type)}</div>
                        {/* SP5: interne Absender zeigen ihr Entra-Foto (wie im Chat). */}
                        <SenderAvatar
                            name={person.name}
                            email={person.email}
                            size={touch ? 36 : 32}
                            bg={empfaengerZeigen ? stringToColor(person.name) : email.senderColor}
                        />
                    </div>
                    )}

                    <div className={styles.mailItemContent}>
                        <div className={styles.mailItemHeader}>
                            <span
                                className={styles.mailSender}
                                style={{ fontWeight: email.read ? 400 : 600 }}
                                title={person.titel}
                            >
                                {person.text}
                            </span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                {/* SP1(Topbar): Quell-Postfach-Chip + Mail-SP2: Ordner-Chip an JEDEM Treffer. */}
                                {searchActive && email.srcMailbox && (() => {
                                    const srcMb = email.srcMailbox as string;
                                    // Ordner-Id nur zeigen, wenn im geladenen Baum auflösbar UND
                                    // nicht der Posteingang (buildFolderNameMap lässt ihn weg).
                                    const folderName = email.parentFolderId
                                        ? folderNameByMailbox[srcMb]?.get(email.parentFolderId)
                                        : undefined;
                                    return (
                                        <>
                                            <span className={styles.mailboxChip}>
                                                {mailboxChipLabel(srcMb)}
                                            </span>
                                            {folderName && (
                                                <span className={styles.folderChip} title={folderName}>
                                                    {folderName}
                                                </span>
                                            )}
                                        </>
                                    );
                                })()}
                                {/* TERMINE (P6): Einladung/Zusage/Absage benennen. Nur
                                    in der Termine-Sicht — im Posteingang stünde an
                                    jeder zweiten Zeile ein Etikett, das der Betreff
                                    ohnehin schon sagt. */}
                                {termineAktiv && terminArtLabel(email.meetingMessageType) && (
                                    <span
                                        className={styles.folderChip}
                                        title={`Termin: ${terminArtLabel(email.meetingMessageType)}`}
                                    >
                                        {terminArtLabel(email.meetingMessageType)}
                                    </span>
                                )}
                                {/* P8 — erwähnt worden: der Grund, warum diese Zeile
                                    einen persönlich angeht. Ungelesen in Gold. */}
                                {erwaehnung !== "keine" && (
                                    <span
                                        className={mergeClasses(
                                            styles.rowMarker,
                                            erwaehnung === "ungelesen" && styles.rowMarkerMention,
                                        )}
                                        role="img"
                                        aria-label={
                                            erwaehnung === "ungelesen"
                                                ? "Sie wurden erwähnt — noch nicht gelesen"
                                                : "Sie wurden erwähnt"
                                        }
                                        title={
                                            erwaehnung === "ungelesen"
                                                ? "Sie wurden erwähnt — noch nicht gelesen"
                                                : "Sie wurden erwähnt"
                                        }
                                    >
                                        {erwaehnung === "ungelesen" ? (
                                            <Mention16Filled />
                                        ) : (
                                            <Mention16Regular />
                                        )}
                                    </span>
                                )}
                                {/* T-212 (Teil B) — Antwort-Entwurf vorhanden: wie in
                                    Outlook „[Entwurf]". Klick setzt den Entwurf fort und
                                    öffnet die Mail NICHT. Im Ordner „Entwürfe" selbst
                                    wäre der Hinweis doppelt. */}
                                {entwurfDerZeile && zeilenMailbox && (
                                    <EntwurfMarker
                                        className={styles.rowMarkerEntwurf}
                                        onFortsetzen={() =>
                                            openEntwurfWindow(openWindow, {
                                                mailbox: zeilenMailbox,
                                                draftId: entwurfDerZeile.draftId,
                                                titel: email.subject,
                                            })
                                        }
                                    />
                                )}
                                {/* P7 (PO 14.09.2026) — gehört zu einem Vorgang. Ein
                                    Ziel hat der Marker auch: Klick öffnet den Vorgang
                                    (bei mehreren den zuletzt verknüpften; der Tooltip
                                    nennt alle). Der Klick darf die Mail nicht öffnen. */}
                                {vorgaengeDerZeile && (
                                    <MailVorgangsMarker
                                        vorgaenge={vorgaengeDerZeile}
                                        className={mergeClasses(styles.rowMarker, styles.rowMarkerVorgang)}
                                        onNavigieren={navigate}
                                    />
                                )}
                                {/* P8 — Anhänge; in einer Konversation über den
                                    ganzen Thread aggregiert (groupConversations). */}
                                {hatAnhang(email) && (
                                    <span
                                        className={styles.rowMarker}
                                        role="img"
                                        aria-label="Enthält Anhänge"
                                        title="Enthält Anhänge"
                                    >
                                        <Attach16Regular />
                                    </span>
                                )}
                                {/* P7 — Anzahl der Nachrichten + Aufklapp-Schalter. */}
                                {aufklappbar && (
                                    <button
                                        type="button"
                                        className={styles.convToggle}
                                        aria-expanded={offen}
                                        aria-label={aufklappLabel(offen, anzahl)}
                                        title={aufklappLabel(offen, anzahl)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleConversation(email);
                                        }}
                                    >
                                        {offen ? <ChevronDown12Regular /> : <ChevronRight12Regular />}
                                        {anzahl}
                                    </button>
                                )}
                                {/* T-65: Papierkorb beim Hovern, an der Stelle der Uhrzeit.
                                    Auch in Kindzeilen — dort ist „diese eine Nachricht
                                    löschen" sogar eindeutiger als an der Kopfzeile. */}
                                {zeigeHoverLoeschen({
                                    touch,
                                    activeFolderId,
                                    snapshotOnly: (email as { snapshotOnly?: boolean }).snapshotOnly,
                                    mailbox: zeilenMailbox,
                                }) && (
                                    <span className={mergeClasses(styles.rowActions, MAIL_ZEILE_AKTIONEN_KLASSE)}>
                                        <MailZeileLoeschen
                                            busy={pendingAction === email.id}
                                            onLoeschen={() => {
                                                if (zeilenMailbox) deleteEmail(zeilenMailbox, email);
                                            }}
                                        />
                                    </span>
                                )}
                                <span className={mergeClasses(styles.mailTime, MAIL_ZEILE_ZEIT_KLASSE)}>
                                    {email.dateLabel}
                                </span>
                            </span>
                        </div>
                        {/* Unter der Kopfzeile wiederholt sich der Betreff nicht —
                            er ist der der Unterhaltung (wie in Outlook Desktop). */}
                        {!kind && (
                        <div
                            className={styles.mailSubject}
                            /* Der Betreff trägt IMMER die volle Tinte — auch gelesen,
                               sonst findet man eine schon gelesene Mail in der Liste
                               nicht wieder. Den Zustand tragen Gewicht und Goldpunkt. */
                            style={{
                                fontWeight: email.read ? 400 : 700,
                                color: INK,
                            }}
                        >
                            {email.subject}
                        </div>
                        )}
                        <div className={styles.previewZeile}>
                            <div
                                className={mergeClasses(
                                    styles.mailPreview,
                                    touch && styles.mailPreviewWrap,
                                )}
                            >
                                {email.preview}
                            </div>
                            {gesendet && <span className={styles.gesendetChip}>Gesendet</span>}
                        </div>
                        {/* Handy: Einladung mit „Wann (Dauer) · Antworten" (Outlook iOS). */}
                        {isPhone &&
                            !kind &&
                            !imAuswahlModus &&
                            zeilenMailbox &&
                            email.meetingMessageType === "meetingRequest" && (
                                <TerminKarte
                                    variante="zeile"
                                    mailbox={zeilenMailbox}
                                    messageId={email.id}
                                    // Wie in der geöffneten Mail (#2385): nach der Antwort weg.
                                    onBeantwortet={() => deleteEmail(zeilenMailbox, email)}
                                />
                            )}
                    </div>
                </div>
            </SwipeToDeleteRow>
        );
    };

    /**
     * P7 — Inhalt unter einer aufgeklappten Konversation: die Nachrichten selbst
     * oder der ehrliche Zwischenzustand (lädt / ging schief / nichts weiter da).
     * Ein aufgeklappter Thread, unter dem nichts steht, wäre schlimmer als der
     * zugeklappte vorher.
     */
    const renderConvChildren = (email: SearchEmail, touch: boolean) => {
        const convId = email.conversationId;
        if (convLoading.has(convId)) {
            return <div className={styles.convHint}>Konversation wird geladen …</div>;
        }
        if (convError.has(convId)) {
            return (
                <div className={styles.convHint}>
                    <span>Konversation konnte nicht geladen werden.</span>
                    <Button
                        appearance="secondary"
                        size="small"
                        onClick={() => {
                            setConvError((prev) => {
                                const next = new Set(prev);
                                next.delete(convId);
                                return next;
                            });
                            ladeKonversation(email);
                        }}
                    >
                        Erneut versuchen
                    </Button>
                </div>
            );
        }
        const kinder = convChildren[convId];
        if (!kinder) return null;
        // Outlook Desktop: ALLE Nachrichten stehen darunter, neueste oben — die
        // der Kopfzeile eingeschlossen (`konversationsZeilen`). Steht dort nur
        // sie selbst, gibt es nichts weiter.
        const zeilen = kinder;
        if (zeilen.length <= 1) {
            return <div className={styles.convHint}>Keine weiteren Nachrichten.</div>;
        }
        return zeilen.map((k) =>
            // Die Kindzeile erbt das Quell-Postfach der Konversation: bei einem
            // Suchtreffer aus fremdem Postfach muss sie dort gelesen werden.
            renderMailRow({ ...k, srcMailbox: email.srcMailbox }, touch, true),
        );
    };

    /* ── Liste (geteilt über alle Breakpoints) ── */
    const renderList = (touch: boolean) => (
        <>
            {/* SP1(Topbar): Desktop/Tablet suchen in der Topbar. Das Phone hat keine
                Topbar — Suche und Filter stehen dort in der Pille des Kopfs
                (MailMobilKopf, wie Outlook iOS); der `searchQuery` ist zentral im
                MailContext, also identischer Stand. */}
            {/* SP2(Filterleiste): an der bisherigen Suchposition (über der Liste) —
                Absender/Empfänger + Zeitraum (Heute/Gestern/Letzte 7 Tage). Sobald
                es etwas zu filtern gibt oder ein Filter aktiv ist (dann bleibt sie
                fürs Zurücksetzen sichtbar). */}
            {!isPhone && (baseEmails.length > 0 || filterActive) && (
                <MailFilterBar
                    value={mailFilter}
                    onChange={setMailFilter}
                    size={touch ? "large" : "medium"}
                />
            )}
            {searchActive && (
                <div className={styles.searchInfo}>
                    {searching
                        ? "Suche läuft …"
                        : `${currentEmails.length} Treffer${readableMailboxes.length > 1 ? " in allen Postfächern" : ""}`}
                </div>
            )}
            {/* L1 — der Filter „Vorgänge" zeigt nicht den Ordner, sondern die
                verknüpften Mails des Postfachs. Das steht dran, damit niemand
                die Liste für den Ordnerinhalt hält. */}
            {nurVorgaenge && (
                <div className={styles.searchInfo}>
                    {vorgangsLoading
                        ? "Vorgangs-Mails werden geladen …"
                        : `${currentEmails.length} ${currentEmails.length === 1 ? "Mail" : "Mails"} an Vorgängen — alle Ordner dieses Postfachs${searchQuery.trim() ? ", Suchtext angewandt" : ""}`}
                </div>
            )}
            {/* SP3 — Refresh-Balken: es wird gefetcht, während (stale) Daten
                sichtbar bleiben — dünner Gold-Läufer wie Outlook. */}
            {(isLoading || syncing) && currentEmails.length > 0 && (
                <div className={styles.refreshBar} aria-label="Aktualisiert …">
                    <div className={styles.refreshBarInner} />
                </div>
            )}
            {/* B4 — Ladefehler sichtbar machen (vorher nur console.error, die
                alte Liste blieb kommentarlos stehen). */}
            {loadError && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                        padding: "10px 16px",
                        background: tokens.colorStatusDangerBackground1,
                        color: tokens.colorStatusDangerForeground1,
                        fontSize: 13,
                    }}
                >
                    <span>E-Mails konnten nicht geladen werden.</span>
                    <Button appearance="secondary" size="small" onClick={() => setRetryTick((t) => t + 1)}>
                        Erneut versuchen
                    </Button>
                </div>
            )}
            {actionError && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        padding: "8px 16px",
                        background: tokens.colorStatusDangerBackground1,
                        color: tokens.colorStatusDangerForeground1,
                        fontSize: 13,
                    }}
                >
                    <span>{actionError}</span>
                    <Button appearance="transparent" size="small" onClick={() => setActionError(null)}>
                        OK
                    </Button>
                </div>
            )}
            {actionNote && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        padding: "8px 16px",
                        background: tokens.colorNeutralBackground3,
                        color: tokens.colorNeutralForeground1,
                        fontSize: 13,
                    }}
                >
                    <span>{actionNote}</span>
                    <Button appearance="transparent" size="small" onClick={() => setActionNote(null)}>
                        OK
                    </Button>
                </div>
            )}
            {undoDelete && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        padding: "8px 16px",
                        background: tokens.colorNeutralBackground3,
                        color: tokens.colorNeutralForeground1,
                        fontSize: 13,
                    }}
                >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        „{undoDelete.subject}" wurde in den Papierkorb verschoben.
                    </span>
                    <Button appearance="transparent" size="small" onClick={doUndoDelete}>
                        Rückgängig
                    </Button>
                </div>
            )}
            {groupedEmails.length === 0 ? (
                (nurVorgaenge
                    ? vorgangsLoading
                    : termineAktiv
                        ? termineLoading
                        : isLoading && !searchActive) ? (
                    // SP3 — Skeleton beim Cold-Load (kein Cache): graue Platzhalter wie Outlook.
                    <div aria-hidden>
                        {Array.from({ length: 9 }, (_, i) => (
                            <div key={i} className={styles.skelRow}>
                                <div className={mergeClasses(styles.skelPulse, styles.skelCircle)} />
                                <div className={styles.skelLines}>
                                    <div
                                        className={mergeClasses(styles.skelPulse, styles.skelLine)}
                                        style={{ width: `${55 + ((i * 17) % 35)}%` }}
                                    />
                                    <div
                                        className={mergeClasses(styles.skelPulse, styles.skelLine)}
                                        style={{ width: `${30 + ((i * 23) % 30)}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.emptyList}>
                        <MailInboxAll20Regular style={{ fontSize: "32px" }} />
                        <span>
                            {termineAktiv
                                ? termineError
                                    ? "Termine konnten nicht geladen werden."
                                    : searchQuery.trim()
                                        ? "Kein Termin passt zu dieser Suche."
                                        : filterActive
                                            ? "Kein Termin passt zu diesem Filter."
                                            : termineMeta?.hasMore
                                                // Der Posteingang ist noch nicht
                                                // zu Ende gesichtet — „nichts da"
                                                // wäre hier gelogen.
                                                ? "Weitere Termine werden gesucht …"
                                                : "Keine Einladungen, Zusagen oder Absagen im Posteingang."
                                : nurVorgaenge
                                ? vorgangsError
                                    ? "Vorgangs-Mails konnten nicht geladen werden."
                                    : vorgangsCursor
                                        // Es kommen noch Seiten (die bisherigen
                                        // hingen an der Vorgangs-Sichtbarkeit) —
                                        // hier wäre „nichts da" schlicht gelogen.
                                        ? "Weitere Vorgangs-Mails werden geladen …"
                                        : searchQuery.trim()
                                            ? "Keine Vorgangs-Mail passt zu dieser Suche."
                                            : mailFilter.text.trim() || mailFilter.range
                                                ? "Keine Vorgangs-Mail passt zu diesem Filter."
                                                : "Keine Mail ist bisher einem Vorgang zugeordnet."
                                : filterActive
                                    ? "Keine E-Mails für diesen Filter."
                                    : searchActive
                                        ? "Keine Treffer."
                                        : "Keine E-Mails in diesem Ordner."}
                        </span>
                        {nurVorgaenge && vorgangsError && currentMailbox && (
                            <Button
                                appearance="secondary"
                                size="small"
                                onClick={() => void ladeVorgangsSeite(currentMailbox, null)}
                            >
                                Erneut versuchen
                            </Button>
                        )}
                        {termineAktiv && termineError && currentMailbox && (
                            <Button
                                appearance="secondary"
                                size="small"
                                onClick={() => void ladeTermineSeite(currentMailbox, null)}
                            >
                                Erneut versuchen
                            </Button>
                        )}
                    </div>
                )
            ) : (
                groupedEmails.map((group) => (
                    <React.Fragment key={group.label}>
                        <div className={styles.dateGroup}>{group.label}</div>
                        {group.emails.map((email) => (
                            <React.Fragment key={email.id}>
                                {renderMailRow(email, touch, false)}
                                {/* P7: aufgeklappt stehen die übrigen Nachrichten
                                    der Konversation eingerückt darunter. */}
                                {expandedConvs.has(email.conversationId) &&
                                    renderConvChildren(email, touch)}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))
            )}
            {/* L1 — Nachladen der Vorgangs-Mails. Der Sentinel löst beim Scrollen
                aus (IntersectionObserver, s. o.); die Schaltfläche bleibt als
                sichtbarer, tastaturbedienbarer Rückfall. */}
            {nurVorgaenge && (
                <div
                    ref={vorgangsSentinelRef}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 6,
                        padding: "12px 16px 16px",
                    }}
                >
                    {vorgangsError && currentEmails.length > 0 && (
                        <span
                            style={{
                                fontSize: 12,
                                color: tokens.colorStatusDangerForeground1,
                                textAlign: "center",
                            }}
                        >
                            Weitere Vorgangs-Mails konnten nicht geladen werden.
                        </span>
                    )}
                    {vorgangsCursor && currentMailbox && (
                        <Button
                            appearance="secondary"
                            size="small"
                            disabled={vorgangsMoreLoading}
                            onClick={() => void ladeVorgangsSeite(currentMailbox, vorgangsCursor)}
                        >
                            {vorgangsMoreLoading ? "Lädt …" : "Mehr laden"}
                        </Button>
                    )}
                    {vorgangsError && currentEmails.length > 0 && currentMailbox && (
                        <Button
                            appearance="secondary"
                            size="small"
                            onClick={() => void ladeVorgangsSeite(currentMailbox, null)}
                        >
                            Erneut versuchen
                        </Button>
                    )}
                </div>
            )}
            {/* TERMINE (P6) — Nachladen der Sicht. Eine Roh-Seite des Posteingangs
                enthält meist nur wenige Termin-Mails; „Mehr laden" sucht die
                nächsten Seiten durch. `hiddenTotal` bleibt dem Posteingang
                vorbehalten — hier wäre es eine andere Größe. */}
            {termineAktiv && (termineMeta?.hasMore || (termineError && currentEmails.length > 0)) && (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 6,
                        padding: "12px 16px 16px",
                    }}
                >
                    {termineError && currentEmails.length > 0 && (
                        <span
                            style={{
                                fontSize: 12,
                                color: tokens.colorStatusDangerForeground1,
                                textAlign: "center",
                            }}
                        >
                            Weitere Termine konnten nicht geladen werden.
                        </span>
                    )}
                    {termineMeta?.hasMore && currentMailbox && (
                        <Button
                            appearance="secondary"
                            size="small"
                            disabled={termineMoreLoading}
                            onClick={() => void ladeTermineSeite(currentMailbox, termineMeta.nextSkip)}
                        >
                            {termineMoreLoading ? "Lädt …" : "Mehr laden"}
                        </Button>
                    )}
                </div>
            )}
            {/* B10 — Transparenz + Paginierung der Ordnerliste: der vorher
                STILLE RBAC-Drop wird als Zähler sichtbar, und >100 Mails sind
                über „Mehr laden" erreichbar (Truncation traf sonst die ältesten).
                T-179: in jedem Ordner, nicht mehr nur im Posteingang.
                Im Vorgangs-Filter irrelevant: dort paginiert die Verknüpfungsliste. */}
            {zeigeOrdnerNachladen && !searchActive && !nurVorgaenge && (hiddenTotal > 0 || unreadMeta?.hasMore) && (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 6,
                        padding: "12px 16px 16px",
                    }}
                >
                    {hiddenTotal > 0 && (
                        <span style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>
                            {hiddenTotal} E-Mail{hiddenTotal === 1 ? "" : "s"} durch Berechtigungen ausgeblendet
                        </span>
                    )}
                    {unreadMeta?.hasMore && (
                        <Button appearance="secondary" size="small" disabled={loadingMore} onClick={handleLoadMore}>
                            {loadingMore ? "Lädt …" : "Mehr laden"}
                        </Button>
                    )}
                </div>
            )}
        </>
    );

    /* ── SP1(Topbar): Funktionsleiste (Desktop + Tablet) —
        [Suchfeld (Breite = Listen-Spalte)] ····· [Synchronisieren] [Einstellungen] ── */
    const renderTopBar = () => (
        <div className={styles.topBar}>
            {/* SP8: @-Erwähnungen-Toggle ganz links (nur mit „Mail besprechen"-Flag);
                Badge zeigt die Zahl ungelesener Erwähnungen. */}
            {mailDiscussOn && (
                <span className={styles.mentionToggle}>
                    <button
                        type="button"
                        className={mergeClasses(styles.topBarBtn, mentionsOpen && styles.topBarBtnActive)}
                        title="Erwähnungen (Mail besprechen)"
                        aria-label="Erwähnungen anzeigen"
                        aria-pressed={mentionsOpen}
                        onClick={() => {
                            setMentionsOpen((o) => {
                                const next = !o;
                                if (next) void loadMentions();
                                return next;
                            });
                        }}
                    >
                        <span className={styles.topBarBtnIcon}>
                            <Mention20Regular />
                        </span>
                    </button>
                    {unreadMentions > 0 && (
                        <span className={styles.mentionBadge}>{unreadMentions > 99 ? "99+" : unreadMentions}</span>
                    )}
                </span>
            )}
            {/* TERMINE (P6): Umschalter „Termine" mit Zähler. Der Ordner-Eintrag in
                der Navigation läuft auf denselben Zustand; hier steht er, weil
                die Funktionsleiste dieser Ansicht gehört. */}
            <span className={styles.mentionToggle}>
                <button
                    type="button"
                    className={mergeClasses(styles.topBarBtn, termineAktiv && styles.topBarBtnActive)}
                    title="Termine (Einladungen, Zusagen, Absagen)"
                    aria-label="Termine anzeigen"
                    aria-pressed={termineAktiv}
                    onClick={() => setTermineOpen((o) => !o)}
                >
                    <span className={styles.topBarBtnIcon}>
                        <CalendarLtr20Regular />
                    </span>
                </button>
                {termineBadge && <span className={styles.mentionBadge}>{termineBadge}</span>}
            </span>
            {/* Cluster ABLAGE (M3): Umschalter „Ablage". Bewusst OHNE Zahl
                (T-294, 12.10.2026): die orange Zahl las sich wie neue Post.
                Was Hand braucht, filtern die Pillen in der Ansicht selbst. */}
            <span className={styles.mentionToggle}>
                <button
                    type="button"
                    className={mergeClasses(styles.topBarBtn, archiveOpen && styles.topBarBtnActive)}
                    title="Ablage (dauerhaft gesicherte Mails)"
                    aria-label="Ablage anzeigen"
                    aria-pressed={archiveOpen}
                    onClick={() => {
                        setArchiveOpen((o) => {
                            const next = !o;
                            if (next) void loadArchive();
                            return next;
                        });
                    }}
                >
                    <span className={styles.topBarBtnIcon}>
                        <Archive20Regular />
                    </span>
                </button>
            </span>
            <MailSearchInput
                className={styles.topBarSearch}
                value={searchQuery}
                onChange={setSearchQuery}
                busy={searching}
                placeholder="E-Mails durchsuchen … (auch from:/to:/subject:)"
            />
            <div className={styles.topBarActions}>
                {/* SP3 — Sync: sofortiger Server-Delta + Refetch (jetzt Icon + Text). */}
                <button
                    type="button"
                    className={styles.topBarBtn}
                    title="Jetzt synchronisieren"
                    disabled={syncing || !currentMailbox}
                    onClick={handleSync}
                >
                    <span className={syncing ? styles.syncSpin : styles.topBarBtnIcon}>
                        <ArrowClockwise20Regular />
                    </span>
                    Synchronisieren
                </button>
                <button
                    type="button"
                    className={styles.topBarBtn}
                    title="Mail-Einstellungen (Vorlagen, Signaturen, Darstellung)"
                    onClick={() => openMailSettingsWindow(openWindow)}
                >
                    <span className={styles.topBarBtnIcon}>
                        {I.settings}
                    </span>
                    Einstellungen
                </button>
            </div>
        </div>
    );

    /* SP8: Die @-Erwähnungen-Ansicht ersetzt (Desktop + Tablet) den Listen-/
       Detail-Bereich, solange sie offen ist. */
    const renderMentions = () => (
        <MailMentionsView
            mentions={mentions}
            loading={mentionsLoading}
            onOpen={openMentionMail}
            onRefresh={loadMentions}
            onClose={() => setMentionsOpen(false)}
        />
    );
    const mentionsActive = mailDiscussOn && mentionsOpen;

    /* Cluster ABLAGE: die Ansicht ersetzt (Desktop + Tablet) den Listen-/
       Detailbereich, solange sie offen ist — wie die Erwähnungen. */
    const renderArchive = () => (
        <MailArchiveView
            items={archiveSichtbar}
            loading={archiveLoading}
            error={archiveError}
            filter={archiveFilter}
            onFilterChange={setArchiveFilter}
            suche={archiveSuche}
            onSucheChange={setArchiveSuche}
            onOpen={oeffneAblageDokument}
            onAssign={weiseAblageKontaktZu}
            onRetry={(item) => void wiederholeAblage(item)}
            onRefresh={() => void loadArchive()}
            onClose={() => setArchiveOpen(false)}
            busyId={archiveBusyId}
        />
    );
    // Die Erwähnungen haben Vorrang, falls beide offen sind — sie sind der
    // ältere, im Alltag häufigere Umschalter.
    const archiveActive = archiveOpen && !mentionsActive;

    // Das Kontextmenü stand bisher NUR im Desktop-Return — Phone und Tablet
    // kehren vorher zurück. Der `onContextMenu`-Handler an der Zeile setzte dort
    // zwar den State, aber nichts rendert ihn. Mobil gab es damit gar keine
    // Zeilenaktion. Als Funktion ausgelagert, damit alle drei Returns dasselbe
    // Menü zeigen; auf Touch-Geräten öffnet es der Long-Press (Android Chrome
    // und iOS Safari feuern dafür `contextmenu`).
    const renderCtxMenu = () => (
        <MailContextMenu
            menu={ctxMenu}
            folderTrees={folderTrees}
            mailboxes={mailboxes}
            hideInbox={isUnreadView}
            hideDelete={activeFolderId === "deleteditems"}
            busy={!!ctxMenu && pendingAction === ctxMenu.email.id}
            onVorgangErstellen={() => starteListenAktion(startVorgangErstellen)}
            onVorgangZuweisen={() => starteListenAktion(startVorgangZuweisen)}
            onSchadenZuordnen={() => starteListenAktion(startSchadenZuordnen)}
            onKontaktZuweisen={() => starteListenAktion(startKontaktZuweisen)}
            onCreateRule={doCreateRule}
            onMoveToInbox={() => doMove("inbox", "Verschieben")}
            onMoveTo={doMoveTo}
            onArchive={doArchive}
            onDelete={doDelete}
            onToggleRead={doToggleRead}
            onClose={() => setCtxMenu(null)}
        />
    );

    /* ── Handy/Tablet: geöffnete Mail als Vollansicht über der Liste ──
       Die Liste bleibt darunter gemountet — „Zurück" landet an derselben
       Scrollstelle. Dieselbe Lese-Ansicht wie im Desktop-Lesebereich. */
    const renderLeseVollansicht = () =>
        readingEmail && paneMailbox ? (
            <div className="mm-lesen">
                {/* Handy: kein eigener Kopf — Zurück laufen die Pfeile der
                    App-Kopfzeile und die iOS-Geste (Verlaufseintrag, s. oben);
                    die Bereiche (Kontakt, Vorgänge, Dokumente, Termine) stehen
                    unter „Mehr" in der Knopfreihe der Mail. Tablet behält den Kopf. */}
                {!isPhone && (
                    <div className="mm-lesen-kopf">
                        <button
                            type="button"
                            className="mm-zurueck"
                            onClick={() => setReadingEmail(null)}
                            aria-label="Zurück zur Liste"
                        >
                            <ChevronLeft24Regular />
                            Zurück
                        </button>
                        <span className="mm-lesen-titel" title={readingEmail.subject || undefined}>
                            {readingEmail.subject || "Kein Betreff"}
                        </span>
                    </div>
                )}
                <div className="mm-lesen-inhalt">
                    <MailDetailContent
                        key={`${readingEmail.id}:${readingEinzeln ? "1" : "u"}`}
                        email={readingEmail}
                        mailbox={paneMailbox}
                        onArchived={removeFromList}
                        singleMessage={readingEinzeln}
                        onCloseRequest={() => setReadingEmail(null)}
                        onOpenMailFromSidebar={(mb, mail) => {
                            setReadingMailbox(mb);
                            setReadingEinzeln(true);
                            setReadingEmail(mail);
                            setSelectedEmailId(mail.id);
                        }}
                        {...(isPhone
                            ? {
                                  leisteVerborgen: true,
                                  aktiverReiter: leseReiter,
                                  onReiterWechsel: setLeseReiter,
                                  kompakt: true,
                                  onVollbild: () => {
                                      openMailDetailWindow(openWindow, {
                                          email: readingEmail,
                                          mailbox: paneMailbox,
                                          onArchived: removeFromList,
                                          singleMessage: readingEinzeln,
                                      });
                                      setReadingEmail(null);
                                  },
                              }
                            : {})}
                    />
                </div>
            </div>
        ) : null;

    /* ── Handy: Auswahlmodus — dieselben Aktionen wie das Kontextmenü ──
       Mehrere Mails laufen NACHEINANDER: Exchange begrenzt die gleichzeitigen
       Verbindungen je Postfach (s. Thread-Index oben). */
    const auswahlEintraege = auswahl ? [...auswahl.values()] : [];
    const beendeAuswahl = () => setAuswahl(null);
    const fuerAlle = (lauf: (mailbox: string, email: SearchEmail) => Promise<void>) => {
        const liste = auswahlEintraege;
        beendeAuswahl();
        void (async () => {
            for (const e of liste) await lauf(e.mailbox, e.email);
        })();
    };
    const sichtbareZeilen = currentEmails
        .map((e) => ({ email: e as SearchEmail, mailbox: (e as SearchEmail).srcMailbox ?? currentMailbox ?? "" }))
        .filter((e) => e.mailbox !== "");
    const auswahlPostfach = auswahl ? gemeinsamesPostfach(auswahl) : null;
    const auswahlOrdner: OrdnerZiel[] | null = auswahlPostfach
        ? buildMailFolderOrder(folderTrees[auswahlPostfach] ?? [], { includeCustom: true })
              .filter((e) => !e.virtual)
              .map((e) => ({ key: e.key, label: e.label, depth: e.depth }))
        : null;
    const setzeLesestatusAuswahl = (gelesen: boolean) =>
        fuerAlle((mb, email) => (email.read === gelesen ? Promise.resolve() : toggleRead(email, mb, gelesen)));
    const renderAuswahlLeiste = () =>
        auswahl ? (
            <MailAuswahlLeiste
                anzahl={auswahl.size}
                alsGelesen={zielLesestatus(auswahl)}
                onLesestatus={() => setzeLesestatusAuswahl(zielLesestatus(auswahl))}
                ordner={auswahlOrdner}
                onVerschieben={(ziel) => fuerAlle((mb, email) => verschiebeMail(mb, email, ziel.key, "Verschieben"))}
                onArchivieren={() => fuerAlle(archiviereMail)}
                onLoeschen={
                    activeFolderId === "deleteditems" ? undefined : () => fuerAlle((mb, email) => deleteEmail(mb, email))
                }
                onMehrEinzeln={() => {
                    // Eine Mail: das Kontextmenü des Desktops mit ALLEN Aktionen
                    // (Vorgang, Kontakt, Schaden, Regel, Verschieben in andere
                    // Postfächer …) — unten rechts, über der Leiste.
                    const [e] = auswahlEintraege;
                    if (!e) return;
                    beendeAuswahl();
                    setCtxMenu({ x: window.innerWidth - 12, y: window.innerHeight - 140, email: e.email, mailbox: e.mailbox });
                }}
                weitere={[
                    ...(isUnreadView
                        ? []
                        : [
                              {
                                  label: "In Posteingang verschieben",
                                  onClick: () => fuerAlle((mb, email) => verschiebeMail(mb, email, "inbox", "Verschieben")),
                              },
                          ]),
                    { label: "Als gelesen markieren", onClick: () => setzeLesestatusAuswahl(true) },
                    { label: "Als ungelesen markieren", onClick: () => setzeLesestatusAuswahl(false) },
                ]}
            />
        ) : null;

    /* ════════════════ PHONE: Outlook-mobil-artig ════════════════
       Oben das Oval (Postfach + Ordner → Seitenmenü) mit Suche und Filter,
       darunter die Liste; unten rechts „Neue Mail". Die App-Navigation unten
       bleibt stehen (NavigationLayout). Langer Druck → Auswahlmodus mit
       Aktionsleiste über der Navigation. */
    if (isPhone) {
        const aktivesPostfach = mailboxes.find((m) => m.mailboxAddress === currentMailbox) ?? null;
        const anderesUngelesen = Object.entries(unreadByMailbox).some(
            ([mb, n]) => mb !== currentMailbox && n > 0 && readableMailboxes.includes(mb),
        );
        const alleAn = !!auswahl && sindAlleAusgewaehlt(auswahl, sichtbareZeilen);
        return (
            <div className={mergeClasses(styles.root, styles.rootPhone)} style={{ position: "relative" }}>
                {auswahl ? (
                    <MailAuswahlKopf
                        anzahl={auswahl.size}
                        alleAn={alleAn}
                        onAlle={() => setAuswahl(alleAn ? new Map() : alleAuswaehlen(sichtbareZeilen))}
                        onAbbrechen={beendeAuswahl}
                    />
                ) : (
                    <MailMobilKopf
                        kuerzel={postfachKuerzel(aktivesPostfach?.displayName, currentMailbox)}
                        postfach={postfachName(aktivesPostfach?.displayName, currentMailbox)}
                        ordner={termineAktiv ? "Termine" : activeFolder?.displayName ?? "Posteingang"}
                        punkt={anderesUngelesen}
                        onMenue={() => setPostfachMenueOffen(true)}
                        suche={searchQuery}
                        onSuche={setSearchQuery}
                        sucheLaeuft={searching}
                        filterAktiv={filterActive}
                        onFilter={() => setFilterSheetOffen(true)}
                    />
                )}
                <div className={mergeClasses(styles.inboxPanel, styles.inboxPanelFluid)}>
                    {renderList(true)}
                    {/* Platz unter der letzten Zeile — dort schweben „Neue Mail"
                        bzw. die Aktionsleiste. */}
                    <div style={{ height: 96, flexShrink: 0 }} aria-hidden />
                </div>
                {!readingEmail && !auswahl && !tastatur && (
                    <NeueMailKnopf
                        disabled={!currentMailbox}
                        onClick={() => openComposeWindow(openWindow, { mailbox: currentMailbox })}
                    />
                )}
                {!readingEmail && renderAuswahlLeiste()}
                {renderLeseVollansicht()}
                <MailPostfachMenue
                    offen={postfachMenueOffen}
                    onSchliessen={() => setPostfachMenueOffen(false)}
                    postfaecher={mailboxes.filter(canReadMailbox)}
                    folderTrees={folderTrees}
                    unreadByMailbox={unreadByMailbox}
                    currentMailbox={currentMailbox}
                    activeFolderId={activeFolderId}
                    isAdmin={isAdmin}
                    onWaehle={(mb, folder) => {
                        if (mb !== currentMailbox) setCurrentMailbox(mb);
                        setActiveFolder(folder);
                    }}
                    onSynchronisieren={currentMailbox ? handleSync : undefined}
                    onEinstellungen={() => openMailSettingsWindow(openWindow)}
                />
                <div className="cal-rd">
                    <BottomSheet
                        open={filterSheetOffen}
                        title="Filter"
                        onClose={() => setFilterSheetOffen(false)}
                    >
                        <MailFilterBar value={mailFilter} onChange={setMailFilter} size="large" />
                    </BottomSheet>
                </div>
                {renderCtxMenu()}
            </div>
        );
    }

    /* ════════════════ TABLET: Liste in voller Breite, Mail als Vollansicht ════════════════
       Statt Liste und Mail gequetscht nebeneinander: die Liste füllt die
       Breite, ein Tipp öffnet die Mail darüber („Zurück" führt zur Liste). */
    if (tabletLayout) {
        // Postfach-/Ordner-Auswahl über das Mail-Menü in der NavLayout-Sidebar (PR4).
        return (
            <div className={styles.root} style={{ position: "relative" }}>
                <div className={styles.centerArea}>
                    {renderTopBar()}
                    {mentionsActive ? (
                        renderMentions()
                    ) : archiveActive ? (
                        renderArchive()
                    ) : (
                        <div className={styles.mainContent}>
                            <div className={mergeClasses(styles.inboxPanel, styles.inboxPanelFluid)}>
                                {renderList(true)}
                            </div>
                        </div>
                    )}
                </div>
                {renderLeseVollansicht()}
                {renderCtxMenu()}
            </div>
        );
    }

    /* ════════════════ DESKTOP: Sidebar + Liste + Detail-Split ════════════════ */
    return (
        <div className={styles.root}>
            {/* ── Center Area (inbox + detail) — die Postfach-/Ordner-Navigation
                 liegt jetzt im Mail-Menü der NavLayout-Sidebar (PR4) ── */}
            <div className={styles.centerArea}>
                {/* SP1(Topbar): Suche + Synchronisieren/Einstellungen (Icon+Text) */}
                {renderTopBar()}
                {/* SP8: Erwähnungen ersetzen Liste + Detail-Split, solange offen. */}
                {mentionsActive ? (
                    renderMentions()
                ) : archiveActive ? (
                    renderArchive()
                ) : (
                <div className={styles.mainContent}>
                    {/* Inbox List */}
                    <div className={styles.inboxPanel}>
                        {renderList(false)}
                    </div>

                    <div className={styles.detailPanel}>
                        {readingEmail && paneMailbox ? (
                            <>
                                {/* SP1: Panel-Kopf — Maximieren öffnet dieselbe Mail als Fenster
                                    (paralleles Arbeiten), Schließen leert das Panel. */}
                                <div className={styles.paneHeader}>
                                    <span className={styles.paneTitle}>
                                        {readingEmail.subject || "Kein Betreff"}
                                    </span>
                                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                                        <Button
                                            appearance="subtle"
                                            size="small"
                                            icon={<ArrowMaximize20Regular />}
                                            title="Als Fenster öffnen"
                                            aria-label="Als Fenster öffnen"
                                            onClick={() => {
                                                openMailDetailWindow(openWindow, {
                                                    email: readingEmail,
                                                    mailbox: paneMailbox,
                                                    onArchived: removeFromList,
                                                    singleMessage: readingEinzeln,
                                                });
                                                setReadingEmail(null);
                                            }}
                                        />
                                        <Button
                                            appearance="subtle"
                                            size="small"
                                            icon={<Dismiss20Regular />}
                                            title="Schließen"
                                            aria-label="Lese-Ansicht schließen"
                                            onClick={() => setReadingEmail(null)}
                                        />
                                    </div>
                                </div>
                                {/* J1: Die rechte Leiste gehört seit Cluster SEITENLEISTE zur
                                    geöffneten Mail selbst — `MailDetailContent` rendert sie, im
                                    Lesepanel wie im Fenster. Hier steht deshalb nur noch der
                                    Container. */}
                                <div style={{ flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                                    <MailDetailContent
                                        key={`${readingEmail.id}:${readingEinzeln ? "1" : "u"}`}
                                        email={readingEmail}
                                        mailbox={paneMailbox}
                                        onArchived={removeFromList}
                                        singleMessage={readingEinzeln}
                                        onCloseRequest={() => setReadingEmail(null)}
                                        /* Im Lesepanel wandert ein Klick in der Leiste INS PANEL
                                           (kein Fenster) — `key={readingEmail.id}` sorgt für den
                                           sauberen Remount. */
                                        onOpenMailFromSidebar={(mb, mail) => {
                                            setReadingMailbox(mb);
                                            setReadingEinzeln(true);
                                            setReadingEmail(mail);
                                            setSelectedEmailId(mail.id);
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className={styles.emptyDetail}>
                                <Mail20Regular style={{ fontSize: "32px" }} />
                                <span>Wählen Sie eine E-Mail aus, um sie hier zu lesen.</span>
                            </div>
                        )}
                    </div>

                </div>
                )}
            </div>

            {renderCtxMenu()}
        </div>
    );
};
